import React from "react";
import { Link } from "react-router-dom";
import WizardStepper from "../../components/WizardStepper";
import AiDisclaimer from "../../components/AiDisclaimer";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import Input from "../../ui/Input";
import RadioCard from "../../ui/RadioCard";
import Badge from "../../ui/Badge";
import Spinner from "../../ui/Spinner";
import Progress from "../../ui/Progress";
import {
  attachGuidelineToJob,
  createJob,
  deleteJobPhotos,
  deleteGuideline,
  downloadEstimatorPack,
  getBuildStatus,
  listGuidelines,
  listJobPhotos,
  lookupPublicSafetyContacts,
  patchJobPhoto,
  reanalyzeJobPhoto,
  fetchJobPhotoBlobUrl,
  pollEstimatorPackReady,
  runBuild,
  updateJobName,
  uploadGuidelines,
  uploadPhotos,
  uploadSketch,
} from "../../api/build";

const STEP_IDS = {
  A0: "A0_PHOTOS_UPLOAD",
  A1: "A1_PHOTO_LABELS",
  A2: "A2_GUIDELINES_UPLOAD",
  A3: "A3_PLATFORM_SELECT",
  A4: "A4_SKETCH_UPLOAD",
  A5: "A5_STATUS_PROCESSING",
  A6: "A6_LABEL_REVIEW",
  A7: "A7_LINE_ITEM_REVIEW",
};

const WIZARD_STEPS = [
  { id: STEP_IDS.A0, label: "Photos Upload" },
  { id: STEP_IDS.A1, label: "Photo Label Choice" },
  { id: STEP_IDS.A3, label: "Platform Select" },
  { id: STEP_IDS.A4, label: "Sketch Upload" },
  { id: STEP_IDS.A5, label: "Status / Processing" },
  { id: STEP_IDS.A6, label: "Label Review" },
  { id: STEP_IDS.A7, label: "Line Item Review" },
];

const PROCESS_PHASES = ["Creating job", "Uploading", "Running", "Packaging", "Ready"];
const STORAGE_KEY = "build_wizard_state_v4";
const LOSS_TYPE_OPTIONS = ["fire", "water", "wind", "vehicle", "vandalism", "theft", "other"];
const CIVIC_CONTACT_LOSS_TYPES = new Set(["fire", "vehicle", "vandalism", "theft"]);

function toTitleCase(value) {
  return String(value || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((x) => x.charAt(0).toUpperCase() + x.slice(1).toLowerCase())
    .join(" ");
}

function createInitialState() {
  return {
    currentStepId: STEP_IDS.A0,
    jobId: "",
    setId: "",
    packRef: null,
    photos: [],
    jobPhotos: [],
    photoIds: [],
    photoSyncError: "",
    wantsPhotoLabels: null,
    wantsLineItemSuggestions: null,
    lossType: "",
    wantsCodeUpgrades: null,
    propertyZip: "",
    guideline: null,
    guidelineDocumentId: "",
    guidelineSkipped: false,
    platform: "",
    sketch: null,
    sketchDocumentId: "",
    sketchSkipped: false,
    processingPhase: "Creating job",
    processingMessage: "",
    processingProgress: 0,
    processingDone: false,
    processingError: "",
    buildRunRequested: false,
    packReady: false,
    downloadingPack: false,
    availableGuidelines: [],
    selectedGuidelineId: "",
    lineItems: [],
    copiedCodeIndex: -1,
    uploadProgress: 0,
    uploadingPhotos: false,
    lineItemsLoading: false,
    lineItemsConfirmedEmpty: false,
    jobInfo: {
      jobName: "",
      insuredName: "",
      claimNumber: "",
      propertyAddress: "",
      notes: "",
      savedAt: "",
    },
  };
}

function safeLoadState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return createInitialState();
    const parsed = JSON.parse(raw);
    const initial = createInitialState();
    const minimalPhotos = Array.isArray(parsed?.photos)
      ? parsed.photos
          .map((p) => ({
            id: p?.id ? String(p.id) : "",
            filename: p?.filename ? String(p.filename) : p?.name ? String(p.name) : "",
            storage_url: p?.storage_url ? String(p.storage_url) : "",
            room: p?.room ?? null,
            damage_type: p?.damage_type ?? null,
            description: p?.description ?? null,
            thumbnail_url: "",
          }))
          .filter((p) => p.id || p.filename || p.storage_url)
      : [];

    return {
      ...initial,
      currentStepId: parsed?.currentStepId || initial.currentStepId,
      jobId: parsed?.jobId || "",
      setId: parsed?.setId || "",
      platform: parsed?.platform || parsed?.selectedPlatform || "",
      guidelineDocumentId: parsed?.guidelineDocumentId || "",
      sketchDocumentId: parsed?.sketchDocumentId || "",
      photoIds: Array.isArray(parsed?.photoIds) ? parsed.photoIds.map((id) => String(id)) : [],
      wantsPhotoLabels:
        typeof parsed?.wantsPhotoLabels === "boolean" ? parsed.wantsPhotoLabels : initial.wantsPhotoLabels,
      wantsLineItemSuggestions:
        typeof parsed?.wantsLineItemSuggestions === "boolean"
          ? parsed.wantsLineItemSuggestions
          : initial.wantsLineItemSuggestions,
      lossType: LOSS_TYPE_OPTIONS.includes(String(parsed?.lossType || "").toLowerCase())
        ? String(parsed?.lossType || "").toLowerCase()
        : "",
      wantsCodeUpgrades:
        typeof parsed?.wantsCodeUpgrades === "boolean" ? parsed.wantsCodeUpgrades : initial.wantsCodeUpgrades,
      propertyZip: typeof parsed?.propertyZip === "string" ? parsed.propertyZip : "",
      photos: minimalPhotos,
      jobPhotos: minimalPhotos,
      jobInfo: {
        ...initial.jobInfo,
        ...(parsed?.jobInfo && typeof parsed.jobInfo === "object" ? parsed.jobInfo : {}),
      },
    };
  } catch (e) {
    return createInitialState();
  }
}

function sanitizeBuildStateForStorage(state, { compact = false } = {}) {
  const sourcePhotos = Array.isArray(state?.jobPhotos) && state.jobPhotos.length > 0
    ? state.jobPhotos
    : Array.isArray(state?.photos)
      ? state.photos
      : [];

  const minimalPhotos = compact
    ? []
    : sourcePhotos
        .map((p) => ({
          id: p?.id ? String(p.id) : "",
          filename: p?.filename ? String(p.filename) : p?.name ? String(p.name) : "",
          storage_url: p?.storage_url ? String(p.storage_url) : "",
          room: p?.room ?? null,
          damage_type: p?.damage_type ?? null,
          description: p?.description ?? null,
        }))
        .filter((p) => p.id || p.filename || p.storage_url);

  return {
    currentStepId: state?.currentStepId || STEP_IDS.A0,
    jobId: state?.jobId || "",
    setId: state?.setId || "",
    platform: state?.platform || "",
    selectedPlatform: state?.platform || "",
    guidelineDocumentId: state?.guidelineDocumentId || "",
    sketchDocumentId: state?.sketchDocumentId || "",
    photoIds: Array.isArray(state?.photoIds) ? state.photoIds.map((id) => String(id)) : [],
    wantsPhotoLabels: typeof state?.wantsPhotoLabels === "boolean" ? state.wantsPhotoLabels : null,
    wantsLineItemSuggestions:
      typeof state?.wantsLineItemSuggestions === "boolean" ? state.wantsLineItemSuggestions : null,
    lossType: LOSS_TYPE_OPTIONS.includes(String(state?.lossType || "").toLowerCase())
      ? String(state?.lossType || "").toLowerCase()
      : "",
    wantsCodeUpgrades: typeof state?.wantsCodeUpgrades === "boolean" ? state.wantsCodeUpgrades : null,
    propertyZip: String(state?.propertyZip || ""),
    photos: minimalPhotos,
    jobInfo: {
      jobName: String(state?.jobInfo?.jobName || ""),
      insuredName: String(state?.jobInfo?.insuredName || ""),
      claimNumber: String(state?.jobInfo?.claimNumber || ""),
      propertyAddress: String(state?.jobInfo?.propertyAddress || ""),
      notes: String(state?.jobInfo?.notes || ""),
      savedAt: String(state?.jobInfo?.savedAt || ""),
    },
  };
}

function isStorageQuotaError(error) {
  const name = String(error?.name || "");
  const code = Number(error?.code || 0);
  const msg = String(error?.message || "").toLowerCase();
  return (
    name === "QuotaExceededError" ||
    name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    code === 22 ||
    code === 1014 ||
    msg.includes("quota")
  );
}

function toFileMeta(file) {
  return {
    name: file.name,
    size: file.size,
    type: file.type,
    thumbnail_url: "",
  };
}

function readFileAsDataUrl(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => resolve("");
    reader.readAsDataURL(file);
  });
}

function moveArrayItem(items, fromIndex, toIndex) {
  const rows = Array.isArray(items) ? [...items] : [];
  if (fromIndex < 0 || fromIndex >= rows.length || toIndex < 0 || toIndex >= rows.length) return rows;
  const [item] = rows.splice(fromIndex, 1);
  rows.splice(toIndex, 0, item);
  return rows;
}

function phaseIndex(phase) {
  const idx = PROCESS_PHASES.indexOf(phase);
  return idx >= 0 ? idx : 0;
}

function resolveThumbUrl(storageUrl) {
  const s = String(storageUrl || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
  return "";
}

function cleanOptional(value) {
  if (value == null) return null;
  const text = String(value).trim();
  if (!text) return null;
  return text;
}

function isPhotoIncomplete(photo) {
  const room = String(photo?.room || "").trim();
  const damageType = String(photo?.damage_type || "").trim();
  const description = String(photo?.description || "").trim();
  const confidence = Number(photo?.confidence);
  return !room || !damageType || !description || !(Number.isFinite(confidence) && confidence > 0);
}

function firstNonEmptyString(...values) {
  for (const value of values) {
    if (value === null || typeof value === "undefined") continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

function getPhotoLabelFields(photo) {
  const analysis =
    photo?.analysis && typeof photo.analysis === "object"
      ? photo.analysis
      : photo?.vision && typeof photo.vision === "object"
        ? photo.vision
        : {};

  const confidenceRaw = firstNonEmptyString(
    photo?.confidence,
    photo?.vision_confidence,
    photo?.vision_confidence_damage,
    photo?.vision_confidence_room,
    analysis?.confidence,
    analysis?.vision_confidence,
    analysis?.vision_confidence_damage,
    analysis?.vision_confidence_room
  );
  const parsedConfidence = Number(confidenceRaw);

  return {
    room: firstNonEmptyString(
      photo?.room,
      photo?.room_label,
      photo?.roomLabel,
      analysis?.room,
      analysis?.room_label,
      analysis?.roomLabel
    ),
    damageType: firstNonEmptyString(
      photo?.damage_type,
      photo?.damageType,
      photo?.damage_label,
      photo?.damageLabel,
      analysis?.damage_type,
      analysis?.damageType,
      analysis?.damage_label,
      analysis?.damageLabel
    ),
    description: firstNonEmptyString(
      photo?.description,
      photo?.damage_description,
      photo?.damageDescription,
      analysis?.description,
      analysis?.damage_description,
      analysis?.damageDescription
    ),
    confidence: Number.isFinite(parsedConfidence) && parsedConfidence > 0 ? parsedConfidence : null,
  };
}

function parseQuantityHintsFromText(text, context = "", analysis = null) {
  const raw = `${String(text || "")} ${String(context || "")}`.toLowerCase();
  const measurements = [];
  let sf = null;
  let lf = null;
  let ea = null;
  let hr = null;

  const pushMax = (curr, next) => {
    if (!Number.isFinite(Number(next))) return curr;
    const n = Number(next);
    if (!Number.isFinite(Number(curr))) return n;
    return Math.max(Number(curr), n);
  };

  const reFindAll = (re) => {
    const out = [];
    let m;
    while ((m = re.exec(raw)) !== null) {
      out.push(m);
    }
    return out;
  };

  reFindAll(/(\d+(?:\.\d+)?)\s*(sf|sq\.?\s*ft|sqft|square\s*feet)\b/g).forEach((m) => {
    const val = Number(m[1]);
    sf = pushMax(sf, val);
    measurements.push({ kind: "area", value: val, unit: "sf", target: "affected area", source: "inferred_text" });
  });

  reFindAll(/(\d+(?:\.\d+)?)\s*(lf|linear\s*feet|lineal\s*feet)\b/g).forEach((m) => {
    const val = Number(m[1]);
    lf = pushMax(lf, val);
    measurements.push({ kind: "length", value: val, unit: "lf", target: "linear extent", source: "inferred_text" });
  });

  reFindAll(/(\d+(?:\.\d+)?)\s*(ea|each)\b/g).forEach((m) => {
    const val = Number(m[1]);
    ea = pushMax(ea, val);
    measurements.push({ kind: "count", value: val, unit: "ea", target: "count", source: "inferred_text" });
  });

  reFindAll(/(\d+(?:\.\d+)?)\s*(hr|hrs|hour|hours)\b/g).forEach((m) => {
    const val = Number(m[1]);
    hr = pushMax(hr, val);
    measurements.push({ kind: "time", value: val, unit: "hr", target: "labor time", source: "inferred_text" });
  });

  // Dimension pair like "4x8" can provide area hint for drywall-like contexts.
  reFindAll(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\b/g).forEach((m) => {
    const a = Number(m[1]);
    const b = Number(m[2]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return;
    if (!/\bdrywall|wall|ceiling|sheetrock|patch|flood cut|flood_cut\b/.test(raw)) return;
    const area = a * b;
    if (area > 0) {
      sf = pushMax(sf, area);
      measurements.push({ kind: "area", value: area, unit: "sf", target: "opening area", source: "inferred_text" });
    }
  });

  if (analysis && Array.isArray(analysis.measurements)) {
    analysis.measurements.forEach((m) => {
      if (!m || typeof m !== "object") return;
      const value = Number(m.value);
      if (!Number.isFinite(value)) return;
      const unit = String(m.unit || "").toLowerCase();
      const kind = String(m.kind || "").toLowerCase();
      if (unit === "sf" || kind === "area") sf = pushMax(sf, value);
      if (unit === "lf" || kind === "length") lf = pushMax(lf, value);
      if (unit === "ea" || kind === "count") ea = pushMax(ea, value);
      if (unit === "hr" || kind === "time") hr = pushMax(hr, value);
      measurements.push({
        kind: kind || "unknown",
        value,
        unit: unit || "unknown",
        target: String(m.target || "measurement"),
        source: String(m.source || "vision"),
      });
    });
  }

  return { sf, lf, ea, hr, measurements };
}

function extractStructuredSignals(photo, labels) {
  const filename = String(photo?.filename || "").toLowerCase();
  const damageType = String(labels?.damageType || "").toLowerCase();
  const desc = String(labels?.description || "").toLowerCase();
  const text = `${filename} ${damageType} ${desc}`;
  const tags = new Set();
  const materialsMissing = [];
  const materialsPresent = [];
  const analysis = photo?.analysis && typeof photo.analysis === "object" ? photo.analysis : {};

  if (/\bwater|leak|stain|wet|moisture|flood\b/.test(text)) {
    tags.add("water");
  }
  if (/\bflood\s*cut|flood_cut|cut\s*out|cutout|demo|opened\s+cavity|open\s+cavity\b/.test(text)) {
    tags.add("flood_cut");
    tags.add("cutout");
  }
  if (/\bdrywall|sheetrock|gyp\b/.test(text)) {
    tags.add("drywall");
    materialsPresent.push("drywall");
  }
  if (/\bwall\b/.test(text)) tags.add("wall");
  if (/\bceiling\b/.test(text)) tags.add("ceiling");
  if (/\binsulation|batt|fiberglass|cellulose|r13|r19|r30\b/.test(text)) {
    tags.add("insulation");
    materialsPresent.push("insulation");
  }
  if (/\bmissing\s+drywall|drywall\s+removed|removed\s+drywall\b/.test(text)) {
    materialsMissing.push("drywall");
    tags.add("missing_drywall");
  }
  if (/\bmissing\s+insulation|insulation\s+removed|removed\s+insulation|wet\s+insulation|saturated\s+insulation\b/.test(text)) {
    materialsMissing.push("insulation");
    tags.add("missing_insulation");
    tags.add("saturated");
  }
  if (/\btoilet\b/.test(text)) tags.add("toilet");
  if (/\bdetach|detached|reset|pull\s+and\s+reset|remove\s+and\s+reset\b/.test(text)) {
    tags.add("detach");
    tags.add("reset");
    tags.add("detach_reset");
  }
  if (/\btub|bathtub|shower\b/.test(text)) tags.add("bathtub");
  if (/\bcontent|contents|furniture|belongings|personal property\b/.test(text)) tags.add("content");
  if (/\bmove|moved|moving|protect|covered|packout|relocate|staged\b/.test(text)) {
    tags.add("move");
    tags.add("manipulation");
    tags.add("protect");
  }
  if (/\borange peel|knockdown|popcorn|smooth|skip trowel|texture\b/.test(text)) tags.add("texture");

  const removedItems = Array.isArray(analysis.removed_items) ? analysis.removed_items : [];
  removedItems.forEach((x) => {
    const s = String(x || "").toLowerCase();
    if (!s) return;
    tags.add(`removed_${s.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")}`);
    if (/drywall|sheetrock/.test(s)) {
      materialsMissing.push("drywall");
      tags.add("missing_drywall");
    }
    if (/insulation|batt|fiberglass|cellulose/.test(s)) {
      materialsMissing.push("insulation");
      tags.add("missing_insulation");
    }
    if (/toilet/.test(s)) tags.add("toilet");
    if (/vanity/.test(s)) tags.add("vanity");
    if (/bathtub|tub/.test(s)) tags.add("bathtub");
    if (/shower/.test(s)) tags.add("shower");
    if (/medicine cabinet|medicine_cabinet/.test(s)) tags.add("medicine_cabinet");
    if (/dishwasher/.test(s)) tags.add("dishwasher");
    if (/refrigerator|fridge/.test(s)) tags.add("refrigerator");
  });

  const suggestedRepairs = Array.isArray(analysis.suggested_repairs) ? analysis.suggested_repairs : [];
  suggestedRepairs.forEach((x) => {
    const s = String(x || "").toLowerCase();
    if (!s) return;
    if (/detach|reset/.test(s)) {
      tags.add("detach");
      tags.add("reset");
      tags.add("detach_reset");
    }
    if (/replace|remove\/replace|r&r/.test(s)) tags.add("replace");
    if (/toilet/.test(s)) tags.add("toilet");
    if (/vanity/.test(s)) tags.add("vanity");
    if (/bathtub|tub/.test(s)) tags.add("bathtub");
    if (/shower/.test(s)) tags.add("shower");
    if (/medicine cabinet|medicine_cabinet/.test(s)) tags.add("medicine_cabinet");
    if (/dishwasher/.test(s)) tags.add("dishwasher");
    if (/refrigerator|fridge/.test(s)) tags.add("refrigerator");
  });

  let finishTexture = "";
  if (/\bknockdown\b/.test(text)) finishTexture = "knockdown";
  else if (/\borange peel\b/.test(text)) finishTexture = "orange_peel";
  else if (/\bpopcorn\b/.test(text)) finishTexture = "popcorn";
  else if (/\bsmooth\b/.test(text)) finishTexture = "smooth";

  return {
    tags: Array.from(tags),
    materialsMissing: Array.from(new Set(materialsMissing)),
    materialsPresent: Array.from(new Set(materialsPresent)),
    finishTexture,
    lossType: tags.has("water") ? "water" : "",
    removedItems: removedItems.map((x) => String(x || "").trim()).filter(Boolean),
    suggestedRepairs: suggestedRepairs.map((x) => String(x || "").trim()).filter(Boolean),
  };
}

function buildObservationsFromJobPhotos(jobPhotos) {
  const rows = Array.isArray(jobPhotos) ? jobPhotos.filter((p) => p && p.id) : [];
  return rows.map((photo) => {
    const labels = getPhotoLabelFields(photo);
    const parsedConfidence = Number(labels.confidence);
    const qty = parseQuantityHintsFromText(labels.description, labels.damageType, photo?.analysis || null);
    const sig = extractStructuredSignals(photo, labels);
    const meta = {};
    if (sig.tags.length > 0) meta.tags = sig.tags;
    if (sig.lossType) meta.loss_type = sig.lossType;
    if (sig.finishTexture) meta.finish_texture = sig.finishTexture;
    if (sig.materialsMissing.length > 0) meta.materials_missing = sig.materialsMissing;
    if (sig.materialsPresent.length > 0) meta.materials_present = sig.materialsPresent;
    if (sig.materialsMissing.includes("drywall")) meta.drywall_missing = true;
    if (sig.materialsMissing.includes("insulation")) meta.insulation_missing = true;
    if (sig.removedItems.length > 0) meta.removed_items = sig.removedItems;
    if (sig.suggestedRepairs.length > 0) meta.suggested_repairs = sig.suggestedRepairs;
    if (Number.isFinite(Number(qty.sf))) meta.sf = Number(qty.sf);
    if (Number.isFinite(Number(qty.lf))) meta.lf = Number(qty.lf);
    if (Number.isFinite(Number(qty.ea))) meta.ea = Math.max(1, Math.round(Number(qty.ea)));
    if (Number.isFinite(Number(qty.hr))) meta.hr = Number(qty.hr);
    if (qty.measurements.length > 0) meta.measurements = qty.measurements;

    return {
      photo_id: String(photo.id),
      room: cleanOptional(photo.room || labels.room),
      damage_type: cleanOptional(labels.damageType),
      description: cleanOptional(labels.description) || "Photo uploaded",
      confidence: Number.isFinite(parsedConfidence) && parsedConfidence > 0 ? parsedConfidence : 0.8,
      needs_review: false,
      meta,
    };
  });
}

function normalizeLineItems(items) {
  const TASK_QTY_FALLBACKS = {
    drywall_remove_replace: 4,
    drywall_tape_float: 4,
    texture_match: 4,
    prime_paint: 16,
    baseboard_remove_replace: 8,
    detach_reset_toilet: 1,
    toilet_remove_replace: 1,
    labor_minimum_vinyl: 1,
    labor_minimum_tile: 1,
    labor_minimum_cleaning: 1,
  };
  const pickQty = (row) => {
    const direct = row?.qty ?? row?.quantity;
    if (direct !== null && typeof direct !== "undefined" && String(direct).trim() !== "") return direct;
    const applied = row?.applied_rules_json || row?.applied_rules;
    let parsed = null;
    if (typeof applied === "string" && applied.trim()) {
      try {
        parsed = JSON.parse(applied);
      } catch (_e) {
        parsed = null;
      }
    } else if (applied && typeof applied === "object") {
      parsed = applied;
    }
    const meta = parsed?.meta || {};
    if (meta.qty_rounded !== null && typeof meta.qty_rounded !== "undefined") return meta.qty_rounded;
    if (meta.qty_raw !== null && typeof meta.qty_raw !== "undefined") return meta.qty_raw;
    const taskId = String(row?.canonical_key || row?.canonical_task_id || "").trim();
    if (taskId && Object.prototype.hasOwnProperty.call(TASK_QTY_FALLBACKS, taskId)) {
      return TASK_QTY_FALLBACKS[taskId];
    }
    const unit = String(row?.unit || "").trim().toUpperCase();
    if (unit === "EA") return 1;
    if (unit === "HR") return 1;
    if (unit === "LF") return 1;
    if (unit === "SF") return 1;
    return "";
  };
  const rows = Array.isArray(items) ? items : [];
  return rows.map((row, index) => ({
    room: String(row?.room || "").trim() || "(unspecified)",
    code: String(row?.platform_code || row?.item_code || row?.code || "").trim(),
    description: String(row?.description || row?.label || "").trim(),
    qty: pickQty(row),
    unit: String(row?.unit || "").trim(),
    confidence: row?.confidence ?? "",
    key: `${index}:${String(row?.id || row?.platform_code || row?.item_code || "row")}`,
  }));
}

function buildFallbackLineItemsFromPhotos(jobPhotos, platform = "xactimate") {
  const rows = Array.isArray(jobPhotos) ? jobPhotos : [];
  const out = [];
  const seen = new Set();
  const add = (code, description, qty = 1, unit = "EA", room = "(unspecified)") => {
    const key = `${room}|${code}|${description}|${unit}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      room,
      code,
      description,
      qty,
      unit,
      confidence: "",
      key,
    });
  };

  const codeWater = platform === "symbility" ? "SYM_MIT_WATER" : "MIT_WATER";
  const codeFloor = platform === "symbility" ? "SYM_FLR_RR" : "FLR_RR";
  const codePaint = platform === "symbility" ? "SYM_PAINT_PP" : "PAINT_PP";
  const codeGeneral = platform === "symbility" ? "SYM_MANUAL_REVIEW" : "MANUAL_REVIEW";

  rows.forEach((p, idx) => {
    const room = String(p?.room || "").trim() || "(unspecified)";
    const txt = `${String(p?.damage_type || "")} ${String(p?.description || "")}`.toLowerCase();
    const hasWater = /water|leak|stain|wet|moisture/.test(txt);
    const hasFloor = /floor|tile|vinyl|plank|carpet|pad|mortar/.test(txt);
    const hasPaint = /paint|prime|texture|drywall|wall|ceiling|trim/.test(txt);

    if (hasWater) add(codeWater, "Water mitigation and dry-out assessment", 1, "EA", room);
    if (hasFloor) add(codeFloor, "Floor covering remove/replace", 1, "EA", room);
    if (hasPaint) add(codePaint, "Prime and paint affected area", 1, "EA", room);
    if (!hasWater && !hasFloor && !hasPaint) {
      add(codeGeneral, `Manual review required for photo ${idx + 1}`, 1, "EA", room);
    }
  });

  return out;
}

function classifyLineItemArea(item) {
  const text = `${String(item?.description || "")} ${String(item?.room || "")}`.toLowerCase();
  if (/ceiling|popcorn|overhead/.test(text)) return "Ceiling";
  if (/floor|tile|vinyl|carpet|pad|plank|mortar|subfloor/.test(text)) return "Floors";
  if (/wall|drywall|paint|texture|baseboard|casing|trim|crown/.test(text)) return "Walls";
  return "Other";
}

function formatRoomName(value) {
  const raw = String(value || "").trim();
  if (!raw) return "(Unspecified)";
  if (raw.startsWith("(") && raw.endsWith(")")) return "(Unspecified)";
  return raw
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

function roomBaseName(value) {
  const label = formatRoomName(value);
  if (!label || label === "(Unspecified)") return "";
  return label.replace(/\s+\d+$/, "").trim();
}

function inferRoomTypeFromText(photo) {
  const text = `${String(photo?.filename || "")} ${String(photo?.description || "")} ${String(photo?.damage_type || "")}`
    .toLowerCase();
  if (/\bbath(room)?\b|\btoilet\b|\bshower\b|\bvanity\b/.test(text)) return "Bathroom";
  if (/\bkitchen\b|\bcabinet\b|\bcounter(top)?\b|\bsink\b/.test(text)) return "Kitchen";
  if (/\bbed(room)?\b/.test(text)) return "Bedroom";
  if (/\bliving\b|\bfamily\b|\bden\b/.test(text)) return "Living Room";
  if (/\bhall(way)?\b|\bcorridor\b/.test(text)) return "Hallway";
  if (/\blaundry\b|\bwasher\b|\bdryer\b/.test(text)) return "Laundry";
  if (/\bgarage\b/.test(text)) return "Garage";
  return "";
}

function normalizeRoomsUnique(photos, { preferSketch = false } = {}) {
  const rows = Array.isArray(photos) ? photos : [];
  const counts = new Map();
  const used = new Set();
  return rows.map((photo, idx) => {
    const raw = String(photo?.room || "").trim();
    const currentBase = roomBaseName(raw);
    let base = currentBase;
    if (!base) {
      const inferred = inferRoomTypeFromText(photo);
      if (inferred) {
        base = inferred;
      } else if (preferSketch) {
        base = "Sketch Room";
      } else {
        base = "Room";
      }
    }

    const n = (counts.get(base) || 0) + 1;
    counts.set(base, n);
    let candidate = n === 1 ? base : `${base} ${n}`;
    while (used.has(candidate)) {
      const next = (counts.get(base) || n) + 1;
      counts.set(base, next);
      candidate = `${base} ${next}`;
    }
    used.add(candidate);

    return {
      ...photo,
      room: candidate,
      room_normalized: candidate,
      room_original: raw || null,
      room_idx: idx + 1,
    };
  });
}

function groupLineItemsByArea(items) {
  const order = ["Walls", "Floors", "Ceiling", "Other"];
  const grouped = { Walls: [], Floors: [], Ceiling: [], Other: [] };
  (Array.isArray(items) ? items : []).forEach((row) => {
    grouped[classifyLineItemArea(row)].push(row);
  });
  return order
    .filter((area) => grouped[area].length > 0)
    .map((area) => ({ area, items: grouped[area] }));
}

function groupLineItemsByRoomAndArea(items) {
  const areaOrder = ["Walls", "Floors", "Ceiling", "Other"];
  const roomOrder = [];
  const roomMap = new Map();
  const rows = Array.isArray(items) ? items : [];
  rows.forEach((row) => {
    const room = formatRoomName(String(row?.room || "").trim() || "(unspecified)");
    if (!roomMap.has(room)) {
      roomMap.set(room, { Walls: [], Floors: [], Ceiling: [], Other: [] });
      roomOrder.push(room);
    }
    const area = classifyLineItemArea(row);
    roomMap.get(room)[area].push(row);
  });
  return roomOrder.map((room) => ({
    room,
    areas: areaOrder
      .filter((area) => roomMap.get(room)[area].length > 0)
      .map((area) => ({ area, items: roomMap.get(room)[area] })),
  }));
}

function mergePhotosWithPreviews(photoIds, jobPhotos, localPhotos) {
  const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);
  const pickBackendText = (row, prior, key) => {
    if (hasOwn(row, key)) return row[key] ?? null;
    return prior?.[key] ?? null;
  };
  const pickText = (primary, fallback) => {
    const p = typeof primary === "string" ? primary.trim() : primary;
    if (p) return primary;
    return fallback ?? null;
  };
  const pickConfidence = (primary, fallback) => {
    const p = Number(primary);
    if (Number.isFinite(p) && p > 0) return p;
    const f = Number(fallback);
    if (Number.isFinite(f) && f > 0) return f;
    return null;
  };
  const localById = new Map(
    (Array.isArray(localPhotos) ? localPhotos : [])
      .filter((p) => p && p.id)
      .map((p) => [String(p.id), p])
  );
  const byId = new Map((Array.isArray(jobPhotos) ? jobPhotos : []).map((p) => [String(p.id), p]));
  const ordered = [];

  (Array.isArray(photoIds) ? photoIds : []).forEach((id, index) => {
    const key = String(id || "");
    if (!key) return;
    const row = byId.get(key);
    const prior = localById.get(key) || {};
    if (!row) return;
    ordered.push({
      ...prior,
      ...row,
      room: pickBackendText(row, prior, "room"),
      damage_type: pickBackendText(row, prior, "damage_type"),
      description: pickBackendText(row, prior, "description"),
      confidence: pickConfidence(row.confidence, prior.confidence),
      thumbnail_url: localPhotos[index]?.thumbnail_url || "",
      analysis_source: pickText(row.analysis_source, prior.analysis_source),
      analysis_error: pickText(row.analysis_error, prior.analysis_error),
    });
    byId.delete(key);
  });

  for (const row of byId.values()) {
    const prior = localById.get(String(row.id)) || {};
    ordered.push({
      ...prior,
      ...row,
      room: pickBackendText(row, prior, "room"),
      damage_type: pickBackendText(row, prior, "damage_type"),
      description: pickBackendText(row, prior, "description"),
      confidence: pickConfidence(row.confidence, prior.confidence),
      thumbnail_url: prior.thumbnail_url || "",
      analysis_source: pickText(row.analysis_source, prior.analysis_source),
      analysis_error: pickText(row.analysis_error, prior.analysis_error),
    });
  }
  return ordered;
}

async function copyText(value) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const el = document.createElement("textarea");
  el.value = value;
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  el.remove();
}

export default function BuildWizard() {
  const [state, setState] = React.useState(() => safeLoadState());
  const [photoFiles, setPhotoFiles] = React.useState([]);
  const [guidelineFile, setGuidelineFile] = React.useState(null);
  const [sketchFile, setSketchFile] = React.useState(null);
  const [copiedLabelIndex, setCopiedLabelIndex] = React.useState(-1);
  const [photoEditDrafts, setPhotoEditDrafts] = React.useState({});
  const [photoSaveState, setPhotoSaveState] = React.useState({});
  const [photoBlobUrls, setPhotoBlobUrls] = React.useState({});
  const [previewImage, setPreviewImage] = React.useState(null);
  const [flash, setFlash] = React.useState({ type: "", text: "" });
  const [packCheckRetrying, setPackCheckRetrying] = React.useState(false);
  const [publicSafetyContacts, setPublicSafetyContacts] = React.useState([]);
  const [publicSafetyLoading, setPublicSafetyLoading] = React.useState(false);
  const [publicSafetyError, setPublicSafetyError] = React.useState("");
  const [publicSafetyResolvedLocation, setPublicSafetyResolvedLocation] = React.useState("");
  const [draggedPhotoIndex, setDraggedPhotoIndex] = React.useState(-1);

  const photosInputRef = React.useRef(null);
  const guidelineInputRef = React.useRef(null);
  const sketchInputRef = React.useRef(null);
  const advanceTimerRef = React.useRef(null);
  const runActiveRef = React.useRef(0);
  const photoBlobUrlsRef = React.useRef({});
  const lastJobIdRef = React.useRef("");

  const refreshJobPhotos = React.useCallback(
    async (jobId, { silent = true } = {}) => {
      if (!jobId) return;
      try {
        const listed = await listJobPhotos(jobId);
        const listedPhotos = Array.isArray(listed?.photos) ? listed.photos : [];
        setState((prev) => {
          const refreshed = mergePhotosWithPreviews(
            prev.photoIds,
            listedPhotos,
            Array.isArray(prev.jobPhotos) && prev.jobPhotos.length > 0 ? prev.jobPhotos : prev.photos
          );
          const normalized = normalizeRoomsUnique(refreshed, {
            preferSketch: Boolean(prev.sketchDocumentId || prev.sketch),
          });
          return { ...prev, jobPhotos: normalized };
        });
      } catch (error) {
        if (!silent) {
          setState((prev) => ({
            ...prev,
            photoSyncError: `Photo list sync failed: ${error?.message || "Unknown error"}`,
          }));
        }
      }
    },
    []
  );

  React.useEffect(() => {
    const payload = sanitizeBuildStateForStorage(state);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      try {
        const fallback = sanitizeBuildStateForStorage(state, { compact: true });
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fallback));
      } catch (fallbackError) {
        const cause = isStorageQuotaError(error) || isStorageQuotaError(fallbackError) ? "quota" : "write";
        console.warn(`[BuildWizard] sessionStorage persistence skipped (${cause})`, fallbackError);
      }
    }
  }, [state]);

  React.useEffect(() => {
    if (!flash.text) return undefined;
    const id = window.setTimeout(() => setFlash({ type: "", text: "" }), 1800);
    return () => window.clearTimeout(id);
  }, [flash]);

  React.useEffect(() => {
    if (!previewImage) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setPreviewImage(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [previewImage]);

  React.useEffect(() => {
    if (lastJobIdRef.current && lastJobIdRef.current !== state.jobId) {
      Object.values(photoBlobUrlsRef.current).forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (_e) {
          // ignore
        }
      });
      photoBlobUrlsRef.current = {};
      setPhotoBlobUrls({});
    }
    lastJobIdRef.current = state.jobId || "";
  }, [state.jobId]);

  React.useEffect(() => {
    let cancelled = false;
    const rows = Array.isArray(state.jobPhotos) ? state.jobPhotos : [];
    rows.forEach((photo) => {
      const photoId = String(photo?.id || "");
      const storageUrl = String(photo?.storage_url || "").trim();
      if (!photoId || !storageUrl || photoBlobUrlsRef.current[photoId]) return;
      fetchJobPhotoBlobUrl(state.jobId, photoId, storageUrl)
        .then((blobUrl) => {
          if (!blobUrl) return;
          if (cancelled) {
            try {
              URL.revokeObjectURL(blobUrl);
            } catch (_e) {
              // ignore
            }
            return;
          }
          photoBlobUrlsRef.current[photoId] = blobUrl;
          setPhotoBlobUrls((prev) => ({ ...prev, [photoId]: blobUrl }));
        })
        .catch(() => {
          // keep local preview fallback
        });
    });
    return () => {
      cancelled = true;
    };
  }, [state.jobId, state.jobPhotos]);

  React.useEffect(
    () => () => {
      Object.values(photoBlobUrlsRef.current).forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (_e) {
          // ignore
        }
      });
      photoBlobUrlsRef.current = {};
    },
    []
  );

  React.useEffect(() => {
    if (!state.jobId) return;
    try {
      const raw = localStorage.getItem(`build_job_info:${state.jobId}`);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.jobInfo || typeof parsed.jobInfo !== "object") return;
      setState((prev) => ({
        ...prev,
        jobInfo: {
          ...prev.jobInfo,
          ...parsed.jobInfo,
          savedAt: parsed.updatedAt || prev.jobInfo?.savedAt || "",
        },
      }));
    } catch (_error) {
      // ignore malformed local job metadata
    }
  }, [state.jobId]);

  const showFlash = React.useCallback((text, type = "success") => {
    setFlash({ type, text });
  }, []);

  React.useEffect(() => {
    if (state.currentStepId !== STEP_IDS.A5) return;
    if (typeof state.wantsLineItemSuggestions !== "boolean") {
      setState((prev) => ({
        ...prev,
        currentStepId: STEP_IDS.A1,
        processingError: "Choose whether to create line item suggestions.",
      }));
      return;
    }
    if (state.processingDone) return;
    if (state.processingError) return;
    runActiveRef.current += 1;
    const runId = runActiveRef.current;

    const run = async () => {
      const setSafe = (updater) => {
        if (runActiveRef.current !== runId) return;
        setState(updater);
      };
      try {
        if (state.wantsLineItemSuggestions === false) {
          setSafe((prev) => ({
            ...prev,
            lineItems: [],
            lineItemsConfirmedEmpty: true,
            packReady: false,
            packRef: null,
            setId: "",
            processingPhase: "Running",
            processingMessage: "Skipping line item suggestions",
            processingProgress: 55,
          }));
          await refreshJobPhotos(state.jobId, { silent: true });
          setSafe((prev) => ({
            ...prev,
            processingDone: true,
            processingError: "",
            processingPhase: "Ready",
            processingProgress: 100,
            processingMessage: "Photo workflow is ready",
          }));
          advanceTimerRef.current = window.setTimeout(() => {
            if (runActiveRef.current !== runId) return;
            setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A6 }));
          }, 600);
          return;
        }

        const shouldSubmitRun = true;
        if (shouldSubmitRun) {
          if (state.guidelineDocumentId) {
            setSafe((prev) => ({
              ...prev,
              processingPhase: "Running",
              processingMessage: "Binding guideline to this job",
              processingProgress: 40,
            }));
            await attachGuidelineToJob(state.jobId, {
              platform: state.platform || "xactimate",
              profileId: "default",
              documentId: state.guidelineDocumentId,
            });
          }
          setSafe((prev) => ({ ...prev, buildRunRequested: true, processingError: "" }));
          let sourcePhotos = Array.isArray(state.jobPhotos) ? state.jobPhotos : [];
          try {
            const listed = await listJobPhotos(state.jobId);
            if (Array.isArray(listed?.photos) && listed.photos.length > 0) {
              sourcePhotos = mergePhotosWithPreviews(state.photoIds, listed.photos, state.photos);
            }
          } catch (_e) {
            // use current in-memory photos if listing fails
          }

          const normalizedPhotos = normalizeRoomsUnique(sourcePhotos, {
            preferSketch: Boolean(state.sketchDocumentId || state.sketch),
          });
          const observations = buildObservationsFromJobPhotos(normalizedPhotos);
          if (observations.length < 1) {
            throw new Error("No backend photo rows found. Go back to Photos Upload and upload photos again.");
          }
          setSafe((prev) => ({
            ...prev,
            jobPhotos: normalizedPhotos,
            processingPhase: "Running",
            processingMessage: "Submitting build request",
            processingProgress: 45,
          }));

          const runResult = await runBuild(state.jobId, {
            platform: state.platform || "xactimate",
            profile_id: "default",
            observations,
            guideline_document_id: state.guidelineDocumentId || undefined,
            sketch_document_id: state.sketchDocumentId || undefined,
            photo_ids: state.photoIds,
            code_upgrades_enabled: Boolean(state.wantsCodeUpgrades),
            property_zip: state.wantsCodeUpgrades ? String(state.propertyZip || "").trim() : undefined,
            loss_cause: state.lossType || undefined,
          });

          const setId = runResult?.setId || "";
          const lineItems = normalizeLineItems(runResult?.raw?.items);
          setSafe((prev) => ({
            ...prev,
            setId,
            lineItems,
            packRef: setId ? { kind: "set", id: setId } : null,
            buildRunRequested: true,
          }));
        }

        setSafe((prev) => ({
          ...prev,
          processingPhase: "Packaging",
          processingMessage: "Checking estimator pack availability",
          processingProgress: 70,
        }));
        const packStatus = await pollEstimatorPackReady(state.jobId, {
          platform: state.platform || "xactimate",
          include_all: true,
          include_rationale: true,
          optimize_entry_order: 1,
          maxAttempts: 60,
          intervalMs: 1000,
          onProgress: (progress) => {
            setSafe((prev) => ({
              ...prev,
              processingPhase: progress.phase || "Packaging",
              processingProgress: Math.max(70, Math.min(99, Number(progress.progress) || 70)),
              processingMessage:
                progress.message ||
                `Checking estimator pack (${progress.attempt || 0}/${progress.maxAttempts || 60})`,
            }));
          },
        });
        if (!packStatus.ready) {
          const latestFallback = await getBuildStatus(state.jobId, {
            platform: state.platform || "xactimate",
          });
          const fallbackItems = normalizeLineItems(latestFallback?.raw?.items);
          setSafe((prev) => ({
            ...prev,
            lineItems: fallbackItems.length > 0 ? fallbackItems : prev.lineItems,
            packRef: { kind: "job", id: state.jobId },
            packReady: false,
            processingDone: true,
            processingError: "",
            processingPhase: "Ready",
            processingProgress: 100,
            processingMessage:
              "Processing finished, but estimator pack is not ready yet. Continue and retry pack check from review.",
          }));
          advanceTimerRef.current = window.setTimeout(() => {
            if (runActiveRef.current !== runId) return;
            setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A6 }));
          }, 600);
          return;
        }
        await refreshJobPhotos(state.jobId, { silent: true });
        const latest = await getBuildStatus(state.jobId, { platform: state.platform || "xactimate" });
        const latestItems = normalizeLineItems(latest?.raw?.items);
        setSafe((prev) => ({
          ...prev,
          lineItems: latestItems.length > 0 ? latestItems : prev.lineItems,
          packRef: packStatus.ref || { kind: "job", id: state.jobId },
        }));

        setSafe((prev) => ({
          ...prev,
          packReady: true,
          processingDone: true,
          processingPhase: "Ready",
          processingProgress: 100,
          processingMessage: "Estimator pack is ready",
        }));

        advanceTimerRef.current = window.setTimeout(() => {
          if (runActiveRef.current !== runId) return;
          setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A6 }));
        }, 600);
      } catch (error) {
        const message = error?.message || "Build failed";
        setSafe((prev) => ({
          ...prev,
          processingError: message,
          processingDone: false,
          processingPhase: "Packaging",
          processingProgress: 0,
          processingMessage: message,
        }));
      }
    };

    run();

    return () => {
      runActiveRef.current += 1;
      if (advanceTimerRef.current) {
        window.clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
    };
  }, [
    state.currentStepId,
    state.guidelineDocumentId,
    state.jobId,
    state.photoIds,
    state.photos,
    state.platform,
    state.propertyZip,
    state.sketchDocumentId,
    state.wantsCodeUpgrades,
    state.wantsLineItemSuggestions,
    refreshJobPhotos,
  ]);

  React.useEffect(() => {
    if (!state.jobId) return;
    if (state.currentStepId !== STEP_IDS.A5 && state.currentStepId !== STEP_IDS.A6) return;
    let active = true;
    const tick = async () => {
      if (!active) return;
      await refreshJobPhotos(state.jobId, { silent: true });
    };
    tick();
    const intervalId = window.setInterval(tick, 3000);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [state.currentStepId, state.jobId, refreshJobPhotos]);

  React.useEffect(() => {
    if (state.currentStepId !== STEP_IDS.A7) return;
    if (state.wantsLineItemSuggestions === false) return;
    if (!state.jobId) return;
    let active = true;
    const loadLatest = async () => {
      setState((prev) => ({ ...prev, lineItemsLoading: true, lineItemsConfirmedEmpty: false }));
      try {
        const platform = state.platform || "xactimate";
        const latest = await getBuildStatus(state.jobId, { platform });
        if (!active) return;
        let items = normalizeLineItems(latest?.raw?.items);
        if (items.length < 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 500));
          if (!active) return;
          const latestRetry = await getBuildStatus(state.jobId, { platform });
          if (!active) return;
          items = normalizeLineItems(latestRetry?.raw?.items);
        }
        if (items.length > 0) {
          setState((prev) => ({
            ...prev,
            lineItems: items,
            lineItemsLoading: false,
            lineItemsConfirmedEmpty: false,
          }));
        } else {
          const fallback = buildFallbackLineItemsFromPhotos(state.jobPhotos, state.platform || "xactimate");
          setState((prev) => ({
            ...prev,
            lineItems: fallback,
            lineItemsLoading: false,
            lineItemsConfirmedEmpty: fallback.length < 1,
          }));
          if (fallback.length > 0) {
            showFlash("No exact matches from engine. Showing provisional line items from photo labels.", "success");
          }
        }
      } catch (_e) {
        if (!active) return;
        setState((prev) => ({
          ...prev,
          lineItemsLoading: false,
          lineItemsConfirmedEmpty: true,
        }));
      }
    };
    loadLatest();
    return () => {
      active = false;
    };
  }, [state.currentStepId, state.jobId, state.platform, state.wantsLineItemSuggestions]);

  React.useEffect(() => {
    if (state.currentStepId !== STEP_IDS.A2) return;
    let active = true;
    const loadGuidelines = async () => {
      try {
        const res = await listGuidelines();
        if (!active) return;
        const docs = Array.isArray(res?.guidelines) ? res.guidelines : [];
        setState((prev) => ({
          ...prev,
          availableGuidelines: docs,
          selectedGuidelineId:
            prev.selectedGuidelineId ||
            (prev.guidelineDocumentId && docs.some((d) => d.id === prev.guidelineDocumentId)
              ? prev.guidelineDocumentId
              : ""),
        }));
      } catch (_e) {
        if (!active) return;
        setState((prev) => ({ ...prev, availableGuidelines: [] }));
      }
    };
    loadGuidelines();
    return () => {
      active = false;
    };
  }, [state.currentStepId, state.guidelineDocumentId, state.selectedGuidelineId]);

  React.useEffect(() => {
    const normalizedLoss = String(state.lossType || "").toLowerCase();
    const shouldLookup =
      state.currentStepId === STEP_IDS.A6 && CIVIC_CONTACT_LOSS_TYPES.has(normalizedLoss);

    if (!shouldLookup) {
      setPublicSafetyContacts([]);
      setPublicSafetyError("");
      setPublicSafetyLoading(false);
      setPublicSafetyResolvedLocation("");
      return;
    }

    let active = true;
    const run = async () => {
      try {
        setPublicSafetyLoading(true);
        setPublicSafetyError("");
        const result = await lookupPublicSafetyContacts({
          lossType: normalizedLoss,
          zipCode: String(state.propertyZip || "").trim(),
          address: String(state.jobInfo?.propertyAddress || "").trim(),
        });
        if (!active) return;
        setPublicSafetyContacts(Array.isArray(result?.contacts) ? result.contacts : []);
        setPublicSafetyResolvedLocation(String(result?.resolved_location || "").trim());
      } catch (e) {
        if (!active) return;
        setPublicSafetyContacts([]);
        setPublicSafetyError(e?.message || "Could not load public safety contacts.");
      } finally {
        if (active) setPublicSafetyLoading(false);
      }
    };

    run();
    return () => {
      active = false;
    };
  }, [state.currentStepId, state.lossType, state.propertyZip, state.jobInfo?.propertyAddress]);

  const setProcessingPhase = (phase, message) => {
    const progressByPhase = {
      "Creating job": 10,
      Uploading: 25,
      Running: 45,
      Packaging: 70,
      Ready: 100,
    };
    setState((prev) => ({
      ...prev,
      processingPhase: phase,
      processingMessage: message || "",
      processingProgress: progressByPhase[phase] ?? prev.processingProgress ?? 0,
    }));
  };

  const handlePhotosChange = async (event) => {
    const files = Array.from(event.target.files || []);
    const previewUrls = await Promise.all(files.map((file) => readFileAsDataUrl(file)));
    setPhotoFiles(files);
    setPhotoEditDrafts({});
    setPhotoSaveState({});
    setState((prev) => ({
      ...prev,
      photos: files.map((file, index) => ({ ...toFileMeta(file), thumbnail_url: previewUrls[index] || "" })),
      jobPhotos: [],
      photoIds: [],
      photoSyncError: "",
      wantsPhotoLabels: null,
      wantsLineItemSuggestions: null,
      lossType: "",
      wantsCodeUpgrades: null,
      propertyZip: "",
      processingError: "",
      processingDone: false,
      processingPhase: "Creating job",
      processingMessage: "",
      processingProgress: 0,
      uploadProgress: 0,
      uploadingPhotos: false,
      buildRunRequested: false,
      packReady: false,
      packRef: null,
      lineItems: [],
      lineItemsConfirmedEmpty: false,
    }));
  };

  const moveSelectedPhoto = React.useCallback((fromIndex, toIndex) => {
    setPhotoFiles((prev) => moveArrayItem(prev, fromIndex, toIndex));
    setState((prev) => ({
      ...prev,
      photos: moveArrayItem(prev.photos, fromIndex, toIndex),
      photoSyncError: "",
      processingError: "",
    }));
  }, []);

  const removeSelectedPhoto = React.useCallback(
    async (index) => {
      const current = Array.isArray(state.photos) ? state.photos : [];
      const photo = current[index];
      const photoId = String(photo?.id || state.photoIds[index] || "").trim();
      if (state.jobId && photoId) {
        try {
          await deleteJobPhotos(state.jobId, [photoId]);
        } catch (error) {
          setState((prev) => ({
            ...prev,
            processingError: error?.message || "Could not delete photo.",
          }));
          showFlash(error?.message || "Could not delete photo.", "error");
          return;
        }
      }

      setPhotoFiles((prev) => prev.filter((_, i) => i !== index));
      setState((prev) => ({
        ...prev,
        photos: prev.photos.filter((_, i) => i !== index),
        jobPhotos: prev.jobPhotos.filter((p, i) => i !== index && (!photoId || String(p?.id || "") !== photoId)),
        photoIds: prev.photoIds.filter((id, i) => i !== index && (!photoId || String(id || "") !== photoId)),
        photoSyncError: "",
        processingError: "",
        processingDone: false,
        packReady: false,
        packRef: null,
        buildRunRequested: false,
      }));
      showFlash("Photo deleted", "success");
    },
    [showFlash, state.jobId, state.photoIds, state.photos]
  );

  const handleSelectedPhotoDrop = (targetIndex) => {
    if (draggedPhotoIndex < 0 || draggedPhotoIndex === targetIndex) {
      setDraggedPhotoIndex(-1);
      return;
    }
    moveSelectedPhoto(draggedPhotoIndex, targetIndex);
    setDraggedPhotoIndex(-1);
  };

  const handleGuidelineChange = (event) => {
    const fileObj = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    setGuidelineFile(fileObj);
    setState((prev) => ({
      ...prev,
      guideline: fileObj ? toFileMeta(fileObj) : null,
      guidelineDocumentId: "",
      guidelineSkipped: !fileObj,
      processingError: "",
    }));
  };

  const handleSketchChange = (event) => {
    const fileObj = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    setSketchFile(fileObj);
    setState((prev) => ({
      ...prev,
      sketch: fileObj ? toFileMeta(fileObj) : null,
      sketchDocumentId: "",
      sketchSkipped: !fileObj,
      processingError: "",
    }));
  };

  const handleA0Continue = async () => {
    try {
      const jobName = String(state.jobInfo?.jobName || "").trim();
      if (!jobName) {
        setState((prev) => ({ ...prev, processingError: "Enter a job name before continuing." }));
        return;
      }
      setProcessingPhase("Creating job", "Ensuring job exists");
      let jobId = state.jobId;
      if (!jobId) {
        const created = await createJob(jobName);
        jobId = created.jobId;
      } else {
        await updateJobName(jobId, jobName);
      }

      setProcessingPhase("Uploading", "Uploading photos");
      let photoIds = state.photoIds;
      let jobPhotos = state.jobPhotos;
      let photoSyncError = "";
      if (photoIds.length < 1) {
        if (photoFiles.length > 0) {
          setState((prev) => ({ ...prev, uploadingPhotos: true, uploadProgress: 0 }));
          const uploaded = await uploadPhotos(jobId, photoFiles, {
            onProgress: (pct) => {
              setState((prev) => ({ ...prev, uploadProgress: pct }));
            },
          });
          photoIds = Array.isArray(uploaded.photoIds) ? uploaded.photoIds : [];
          jobPhotos = Array.isArray(uploaded.photos) ? uploaded.photos : [];
          try {
            const listed = await listJobPhotos(jobId);
            jobPhotos = Array.isArray(listed?.photos) ? listed.photos : [];
            photoIds = jobPhotos.map((p) => p.id).filter(Boolean);
          } catch (e) {
            photoSyncError = `Photo list sync failed: ${e?.message || "Unknown error"}`;
          }
          showFlash("Photos uploaded", "success");
        } else {
          photoIds = state.photos.map((_, idx) => `photo_${idx + 1}`);
          photoSyncError = "Unable to sync photos from backend.";
        }
      }
      if (jobPhotos.length < 1 && !photoSyncError) {
        photoSyncError = "Unable to sync photos from backend.";
      }
      const hydratedPhotos = normalizeRoomsUnique(
        mergePhotosWithPreviews(photoIds, jobPhotos, state.photos),
        { preferSketch: Boolean(state.sketchDocumentId || state.sketch) }
      );

      setState((prev) => ({
        ...prev,
        jobId,
        photoIds,
        jobPhotos: hydratedPhotos,
        photoSyncError,
        currentStepId: STEP_IDS.A1,
        processingError: "",
        uploadingPhotos: false,
        uploadProgress: 100,
      }));
    } catch (error) {
      const message = error?.message || "Photo upload failed";
      setState((prev) => ({
        ...prev,
        processingError: message,
        uploadingPhotos: false,
      }));
      showFlash(message, "error");
    }
  };

  const analyzeUploadedPhotosForLabels = async () => {
    if (!state.jobId) {
      throw new Error("Cannot label photos: missing job id.");
    }
    let rows = Array.isArray(state.jobPhotos) ? state.jobPhotos : [];
    try {
      const listed = await listJobPhotos(state.jobId);
      if (Array.isArray(listed?.photos) && listed.photos.length > 0) {
        rows = mergePhotosWithPreviews(state.photoIds, listed.photos, state.photos);
      }
    } catch (error) {
      throw new Error(`Photo list sync failed before labeling: ${error?.message || "Unknown error"}`);
    }
    const photoRows = rows.filter((photo) => photo?.id);
    if (photoRows.length < 1) {
      throw new Error("No uploaded photos were found to label. Go back and upload photos again.");
    }

    const analyzedRows = [];
    let failures = 0;
    for (let index = 0; index < photoRows.length; index += 1) {
      const photo = photoRows[index];
      if (String(photo?.analysis_source || "").toLowerCase() === "manual_user") {
        analyzedRows.push(photo);
        continue;
      }
      setState((prev) => ({
        ...prev,
        processingPhase: "Labeling",
        processingMessage: `Labeling photo ${index + 1} of ${photoRows.length}`,
        processingProgress: Math.max(10, Math.round(((index + 1) / photoRows.length) * 90)),
        processingError: "",
      }));
      try {
        // eslint-disable-next-line no-await-in-loop
        const analyzed = await reanalyzeJobPhoto(state.jobId, photo.id);
        if (!analyzed.room && !analyzed.damage_type && !analyzed.description) {
          failures += 1;
          analyzedRows.push({
            ...photo,
            analysis_error:
              analyzed.error ||
              "Vision did not return labels for this photo. Review and label manually.",
          });
        } else {
          analyzedRows.push({
            ...photo,
            ...analyzed,
            thumbnail_url: photo.thumbnail_url || resolveThumbUrl(photo.storage_url),
          });
        }
      } catch (error) {
        failures += 1;
        analyzedRows.push({
          ...photo,
          analysis_error: error?.message || "Photo analysis failed. Review and label manually.",
        });
      }
    }

    setState((prev) => ({
      ...prev,
      jobPhotos: normalizeRoomsUnique(analyzedRows, {
        preferSketch: Boolean(prev.sketchDocumentId || prev.sketch),
      }),
      photoSyncError:
        failures > 0
          ? `${failures} photo${failures === 1 ? "" : "s"} could not be labeled automatically. Review and label manually.`
          : "",
      processingPhase: "Ready",
      processingMessage: failures > 0 ? "Photo labeling finished with review items" : "Photo labels are ready",
      processingProgress: 100,
    }));
  };

  const handleA1Continue = async () => {
    if (typeof state.wantsLineItemSuggestions !== "boolean") {
      setState((prev) => ({
        ...prev,
        processingError: "Choose whether to create line item suggestions.",
      }));
      return;
    }
    if (!LOSS_TYPE_OPTIONS.includes(String(state.lossType || "").toLowerCase())) {
      setState((prev) => ({
        ...prev,
        processingError: "Select a type of loss to continue.",
      }));
      return;
    }
    if (state.wantsLineItemSuggestions && state.wantsCodeUpgrades) {
      const zip = String(state.propertyZip || "").trim();
      if (!/^\d{5}(-\d{4})?$/.test(zip)) {
        setState((prev) => ({
          ...prev,
          processingError: "Enter a valid ZIP code (e.g., 12345) for code upgrades.",
        }));
        return;
      }
    }
    try {
      if (state.wantsPhotoLabels) {
        await analyzeUploadedPhotosForLabels();
      }
    } catch (error) {
      const message = error?.message || "Photo labeling failed";
      setState((prev) => ({
        ...prev,
        processingError: message,
        processingPhase: "Labeling",
        processingMessage: message,
        processingProgress: 0,
      }));
      showFlash(message, "error");
      return;
    }
    setState((prev) => ({
      ...prev,
      currentStepId: state.wantsLineItemSuggestions ? STEP_IDS.A3 : STEP_IDS.A5,
      guideline: null,
      guidelineDocumentId: "",
      guidelineSkipped: true,
      ...(state.wantsLineItemSuggestions
        ? {}
        : {
            wantsCodeUpgrades: false,
            propertyZip: "",
            guideline: null,
            guidelineDocumentId: "",
            guidelineSkipped: true,
            platform: "",
            sketch: null,
            sketchDocumentId: "",
            sketchSkipped: true,
            buildRunRequested: false,
            processingDone: false,
            packReady: false,
            packRef: null,
            lineItems: [],
            lineItemsConfirmedEmpty: true,
          }),
      processingError: "",
    }));
  };

  const handleA2Continue = async () => {
    if (state.selectedGuidelineId) {
      setState((prev) => ({
        ...prev,
        guidelineDocumentId: state.selectedGuidelineId,
        guidelineSkipped: false,
        currentStepId: STEP_IDS.A3,
        processingError: "",
      }));
      showFlash("Guideline selected", "success");
      return;
    }
    if (!state.guideline) {
      setState((prev) => ({
        ...prev,
        guidelineSkipped: true,
        guidelineDocumentId: "",
        currentStepId: STEP_IDS.A3,
      }));
      return;
    }
    try {
      setProcessingPhase("Uploading", "Uploading guidelines");
      const uploaded = await uploadGuidelines(state.jobId, guidelineFile);
      const guidelineDocumentId = uploaded.guidelineDocumentId || `guideline_${state.guideline.name}`;
      let docs = state.availableGuidelines;
      try {
        const listed = await listGuidelines();
        docs = Array.isArray(listed?.guidelines) ? listed.guidelines : docs;
      } catch (_e) {
        // ignore
      }
      setState((prev) => ({
        ...prev,
        availableGuidelines: docs,
        selectedGuidelineId: guidelineDocumentId,
        guidelineDocumentId,
        guidelineSkipped: false,
        currentStepId: STEP_IDS.A3,
        processingError: "",
      }));
      showFlash(uploaded?.reused ? "Guideline already exists. Reused saved copy." : "Guideline uploaded", "success");
    } catch (error) {
      const message = error?.message || "Guideline upload failed.";
      setState((prev) => ({ ...prev, processingError: message }));
      showFlash(message, "error");
      return;
    }
  };

  const handleDeleteSelectedGuideline = async () => {
    if (!state.selectedGuidelineId) return;
    try {
      await deleteGuideline(state.selectedGuidelineId);
      const listed = await listGuidelines();
      const docs = Array.isArray(listed?.guidelines) ? listed.guidelines : [];
      setState((prev) => ({
        ...prev,
        availableGuidelines: docs,
        selectedGuidelineId: "",
        guidelineDocumentId: prev.guidelineDocumentId === state.selectedGuidelineId ? "" : prev.guidelineDocumentId,
      }));
      showFlash("Guideline deleted", "success");
    } catch (error) {
      setState((prev) => ({ ...prev, processingError: error?.message || "Could not delete guideline" }));
    }
  };

  const skipGuideline = () => {
    if (guidelineInputRef.current) guidelineInputRef.current.value = "";
    setGuidelineFile(null);
    setState((prev) => ({
      ...prev,
      guideline: null,
      selectedGuidelineId: "",
      guidelineDocumentId: "",
      guidelineSkipped: true,
      currentStepId: STEP_IDS.A3,
    }));
  };

  const handleA3Continue = () => {
    setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A4 }));
  };

  const handleA4Continue = async () => {
    if (!state.sketch) {
      setState((prev) => ({
        ...prev,
        sketchSkipped: true,
        sketchDocumentId: "",
        currentStepId: STEP_IDS.A5,
        buildRunRequested: false,
        processingDone: false,
      }));
      return;
    }
    try {
      setProcessingPhase("Uploading", "Uploading sketch");
      const uploaded = await uploadSketch(state.jobId, sketchFile);
      const sketchDocumentId = uploaded.sketchDocumentId || `sketch_${state.sketch.name}`;
      setState((prev) => ({
        ...prev,
        sketchDocumentId,
        sketchSkipped: false,
        currentStepId: STEP_IDS.A5,
        buildRunRequested: false,
        processingDone: false,
        processingError: "",
      }));
      showFlash("Sketch uploaded", "success");
    } catch (error) {
      setState((prev) => ({
        ...prev,
        sketchDocumentId: `sketch_${state.sketch?.name || "local"}`,
        currentStepId: STEP_IDS.A5,
        buildRunRequested: false,
        processingDone: false,
        processingError: error?.message || "Sketch upload failed. Continuing.",
      }));
    }
  };

  const skipSketch = () => {
    if (sketchInputRef.current) sketchInputRef.current.value = "";
    setSketchFile(null);
    setState((prev) => ({
      ...prev,
      sketch: null,
      sketchDocumentId: "",
      sketchSkipped: true,
      currentStepId: STEP_IDS.A5,
      buildRunRequested: false,
      processingDone: false,
      processingError: "",
    }));
  };

  const retryBuild = () => {
    setState((prev) => ({
      ...prev,
      buildRunRequested: false,
      processingError: "",
      processingDone: false,
      processingPhase: "Running",
      processingProgress: 35,
      processingMessage: "Retrying build",
      packReady: false,
      packRef: null,
    }));
  };

  const handleStepJump = (stepId) => {
    const stepIndex = WIZARD_STEPS.findIndex((step) => step.id === stepId);
    const lineItemChoiceIndex = WIZARD_STEPS.findIndex((step) => step.id === STEP_IDS.A1);
    if (stepIndex > lineItemChoiceIndex && typeof state.wantsLineItemSuggestions !== "boolean") {
      return;
    }
    if (
      state.wantsLineItemSuggestions === false &&
      [STEP_IDS.A2, STEP_IDS.A3, STEP_IDS.A4, STEP_IDS.A7].includes(stepId)
    ) {
      return;
    }
    setState((prev) => ({
      ...prev,
      currentStepId: stepId,
      processingError: "",
      ...(stepId === STEP_IDS.A5 ? { buildRunRequested: false, processingDone: false } : {}),
    }));
  };

  const handleCancelBuild = () => {
    runActiveRef.current += 1;
    if (photosInputRef.current) photosInputRef.current.value = "";
    if (guidelineInputRef.current) guidelineInputRef.current.value = "";
    if (sketchInputRef.current) sketchInputRef.current.value = "";
    setPhotoFiles([]);
    setGuidelineFile(null);
    setSketchFile(null);
    setCopiedLabelIndex(-1);
    setPhotoEditDrafts({});
    setPhotoSaveState({});
    setDraggedPhotoIndex(-1);
    setState(createInitialState());
  };

  const handleCopyLabel = async (label, index) => {
    try {
      await copyText(label);
      setCopiedLabelIndex(index);
      window.setTimeout(() => setCopiedLabelIndex(-1), 1000);
    } catch (e) {
      setState((prev) => ({
        ...prev,
        processingError: "Could not copy label to clipboard",
      }));
    }
  };

  const handleDownloadPack = async () => {
    try {
      setState((prev) => ({ ...prev, downloadingPack: true, processingError: "" }));
      const downloadRef = state.packRef || (state.jobId ? { kind: "job", id: state.jobId } : null);
      const blob = await downloadEstimatorPack(downloadRef, {
        platform: state.platform || "xactimate",
        includeAll: true,
        includeRationale: true,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estimator-pack-${state.jobId || "job"}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showFlash("Estimator pack downloaded", "success");
    } catch (error) {
      setState((prev) => ({
        ...prev,
        processingError: error?.message || "Download failed",
      }));
    } finally {
      setState((prev) => ({ ...prev, downloadingPack: false }));
    }
  };

  const startAnotherBuild = () => {
    if (photosInputRef.current) photosInputRef.current.value = "";
    if (guidelineInputRef.current) guidelineInputRef.current.value = "";
    if (sketchInputRef.current) sketchInputRef.current.value = "";
    setPhotoFiles([]);
    setGuidelineFile(null);
    setSketchFile(null);
    setCopiedLabelIndex(-1);
    setPhotoEditDrafts({});
    setPhotoSaveState({});
    setDraggedPhotoIndex(-1);
    setState(createInitialState());
  };

  const handlePhotoDraftChange = (photoId, field, value) => {
    setPhotoEditDrafts((prev) => ({
      ...prev,
      [photoId]: {
        ...(prev[photoId] || {}),
        [field]: value,
      },
    }));
  };

  const handleSavePhoto = async (photo) => {
    if (!photo?.id) return;
    const draft = photoEditDrafts[photo.id] || {};
    const payload = {
      room: Object.prototype.hasOwnProperty.call(draft, "room") ? draft.room : photo.room,
      damage_type: Object.prototype.hasOwnProperty.call(draft, "damage_type") ? draft.damage_type : photo.damage_type,
      description: Object.prototype.hasOwnProperty.call(draft, "description") ? draft.description : photo.description,
    };
    setPhotoSaveState((prev) => ({
      ...prev,
      [photo.id]: { saving: true, error: "" },
    }));
    setState((prev) => ({
      ...prev,
      jobPhotos: prev.jobPhotos.map((p) => (p.id === photo.id ? { ...p, ...payload } : p)),
    }));
    try {
      const updated = await patchJobPhoto(state.jobId, photo.id, payload);
      setState((prev) => ({
        ...prev,
        jobPhotos: prev.jobPhotos.map((p) =>
          p.id === photo.id
            ? {
                ...p,
                ...updated,
                thumbnail_url: p.thumbnail_url || resolveThumbUrl(updated.storage_url),
              }
            : p
        ),
        setId: "",
        packRef: null,
        packReady: false,
        buildRunRequested: false,
      }));
      await refreshJobPhotos(state.jobId, { silent: true });
      setPhotoSaveState((prev) => ({
        ...prev,
        [photo.id]: { saving: false, error: "" },
      }));
      showFlash("Label saved", "success");
      setPhotoEditDrafts((prev) => {
        const next = { ...prev };
        delete next[photo.id];
        return next;
      });
    } catch (error) {
      setPhotoSaveState((prev) => ({
        ...prev,
        [photo.id]: { saving: false, error: error?.message || "Save failed" },
      }));
    }
  };

  const handleSaveAllPhotos = async () => {
    const rows = summaryPhotoRows.filter((p) => p?.id);
    for (const photo of rows) {
      // eslint-disable-next-line no-await-in-loop
      await handleSavePhoto(photo);
    }
    showFlash("All labels saved", "success");
  };

  const handleCopyLineItemCode = async (code, index) => {
    try {
      await copyText(code || "");
      setState((prev) => ({ ...prev, copiedCodeIndex: index }));
      window.setTimeout(() => {
        setState((prev) => ({ ...prev, copiedCodeIndex: -1 }));
      }, 1000);
    } catch (_e) {
      setState((prev) => ({ ...prev, processingError: "Could not copy line item code to clipboard" }));
    }
  };

  const handleReanalyze = async (photo = null) => {
    if (photo?.id) {
      if (!state.jobId) {
        const msg = "Cannot re-analyze: missing job id.";
        setState((prev) => ({ ...prev, processingError: msg }));
        showFlash(msg, "error");
        return;
      }
      setPhotoSaveState((prev) => ({
        ...prev,
        [photo.id]: { ...(prev[photo.id] || {}), saving: true, reanalyzing: true, error: "" },
      }));
      showFlash("Re-analyzing photo...", "success");
      try {
        const analyzed = await reanalyzeJobPhoto(state.jobId, photo.id);
        if (!analyzed.room && !analyzed.damage_type && !analyzed.description) {
          throw new Error(
            analyzed.error ||
              "Vision did not return labels for this photo. Check backend vision configuration and try again."
          );
        }
        setState((prev) => ({
          ...prev,
          jobPhotos: prev.jobPhotos.map((p) =>
            p.id === photo.id
              ? {
                  ...p,
                  ...analyzed,
                  thumbnail_url: p.thumbnail_url || resolveThumbUrl(p.storage_url),
                }
              : p
          ),
          setId: "",
          packRef: null,
          packReady: false,
          buildRunRequested: false,
        }));
        setPhotoEditDrafts((prev) => {
          const next = { ...prev };
          delete next[photo.id];
          return next;
        });
        await refreshJobPhotos(state.jobId, { silent: true });
        setPhotoSaveState((prev) => ({
          ...prev,
          [photo.id]: { ...(prev[photo.id] || {}), saving: false, reanalyzing: false, error: "" },
        }));
        showFlash("Photo re-analyzed", "success");
      } catch (error) {
        const msg = error?.message || "Photo re-analysis failed";
        setState((prev) => ({ ...prev, processingError: msg }));
        setPhotoSaveState((prev) => ({
          ...prev,
          [photo.id]: { ...(prev[photo.id] || {}), saving: false, reanalyzing: false, error: msg },
        }));
        showFlash(msg, "error");
      }
      return;
    }

    setState((prev) => ({
      ...prev,
      currentStepId: STEP_IDS.A5,
      setId: "",
      packRef: null,
      buildRunRequested: false,
      processingDone: false,
      processingError: "",
      processingPhase: "Running",
      processingProgress: 35,
      processingMessage: "Re-analyzing results",
      packReady: false,
    }));
    showFlash("Re-analysis started", "success");
  };

  const handleRetryPackCheck = async () => {
    if (!state.jobId || packCheckRetrying) return;
    try {
      setPackCheckRetrying(true);
      setState((prev) => ({
        ...prev,
        processingError: "",
        processingPhase: "Packaging",
        processingMessage: "Retrying estimator pack check",
      }));
      const packStatus = await pollEstimatorPackReady(state.jobId, {
        platform: state.platform || "xactimate",
        include_all: true,
        include_rationale: true,
        optimize_entry_order: 1,
        maxAttempts: 20,
        intervalMs: 1000,
      });
      if (packStatus.ready) {
        setState((prev) => ({
          ...prev,
          packReady: true,
          packRef: packStatus.ref || { kind: "job", id: state.jobId },
          processingPhase: "Ready",
          processingProgress: 100,
          processingMessage: "Estimator pack is ready",
        }));
        showFlash("Estimator pack is ready", "success");
        return;
      }
      setState((prev) => ({
        ...prev,
        processingError: packStatus.error || "Estimator pack is not ready yet. Try again shortly.",
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        processingError: error?.message || "Estimator pack check failed",
      }));
    } finally {
      setPackCheckRetrying(false);
    }
  };

  const handleJobInfoChange = (field, value) => {
    setState((prev) => ({
      ...prev,
      jobInfo: {
        ...(prev.jobInfo || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveJobInfo = async () => {
    try {
      const jobName = String(state.jobInfo?.jobName || "").trim();
      if (!jobName) {
        showFlash("Job name is required", "error");
        return;
      }
      if (state.jobId) {
        await updateJobName(state.jobId, jobName);
      }
      const payload = {
        jobId: state.jobId || "",
        setId: state.setId || "",
        updatedAt: new Date().toISOString(),
        jobInfo: state.jobInfo || {},
      };
      if (payload.jobId) {
        localStorage.setItem(`build_job_info:${payload.jobId}`, JSON.stringify(payload));
      }
      setState((prev) => ({
        ...prev,
        jobInfo: {
          ...(prev.jobInfo || {}),
          savedAt: payload.updatedAt,
        },
      }));
      showFlash("Job information saved", "success");
    } catch (_error) {
      showFlash("Could not save job information locally", "error");
    }
  };

  const summaryPhotoRows = React.useMemo(() => {
    if (!Array.isArray(state.jobPhotos) || state.jobPhotos.length < 1) return [];
    const ordered = mergePhotosWithPreviews(state.photoIds, state.jobPhotos, state.photos);
    return ordered.map((photo, index) => {
      const labels = getPhotoLabelFields(photo);
      const room = String(labels.room || "").trim();
      const damageType = String(labels.damageType || "").trim();
      const description = String(labels.description || "").trim();
      const displayRoom = formatRoomName(room || "(room not set)");
      const displayDamageType = damageType || "(damage type not set)";
      const displayDescription = description || "(description not set)";
      const idSuffix = state.photoIds[index] ? ` [${state.photoIds[index]}]` : "";
      return {
        ...photo,
        displayRoom,
        displayDamageType,
        displayDescription,
        label: `Room: ${displayRoom} | Damage Type: ${displayDamageType} | Damage: ${displayDescription}${idSuffix}`,
        thumbnail_url: photo.thumbnail_url || "",
        confidence: labels.confidence,
      };
    });
  }, [state.jobPhotos, state.photoIds, state.photos]);

  const labelCompleteness = React.useMemo(() => {
    const rows = Array.isArray(summaryPhotoRows) ? summaryPhotoRows : [];
    const total = rows.length;
    const complete = rows.filter((photo) => {
      const room = String(photo?.room || "").trim();
      const damageType = String(photo?.damage_type || "").trim();
      const description = String(photo?.description || "").trim();
      return room && damageType && description;
    }).length;
    const percent = total > 0 ? Math.round((complete / total) * 100) : 0;
    return { total, complete, remaining: Math.max(0, total - complete), percent };
  }, [summaryPhotoRows]);

  const hasUnsavedPhotoDrafts = React.useMemo(() => Object.keys(photoEditDrafts).length > 0, [photoEditDrafts]);
  const groupedLineItemsByRoom = React.useMemo(
    () => groupLineItemsByRoomAndArea(state.lineItems),
    [state.lineItems]
  );
  const renderA0 = () => (
    <Card className="wizard-card wizard-card-centered">
      <CardHeader>
        <h1 className="wizard-title">Photos Upload</h1>
        <p className="wizard-subtitle">Upload photos to start building an estimate.</p>
      </CardHeader>
      <CardBody>
      <div className="mb-4">
        <Input
          value={state.jobInfo?.jobName || ""}
          placeholder="Job name (required)"
          maxLength={255}
          onChange={(e) => handleJobInfoChange("jobName", e.target.value)}
        />
      </div>
      <input
        ref={photosInputRef}
        id="wizard-photos-upload"
        type="file"
        multiple
        accept="image/*"
        className="wizard-hidden-input"
        onChange={handlePhotosChange}
      />
      <label className="wizard-upload-button" htmlFor="wizard-photos-upload">
        Upload Photos
      </label>
      {state.photos.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {state.photos.map((f, idx) => (
            <div
              key={`${f.name}-${f.size}-${idx}`}
              className={[
                "overflow-hidden rounded-xl border bg-white transition",
                draggedPhotoIndex === idx ? "border-zinc-900 opacity-70" : "border-zinc-200",
              ].join(" ")}
              title={f.name || `Photo ${idx + 1}`}
              draggable
              onDragStart={(e) => {
                setDraggedPhotoIndex(idx);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", String(idx));
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
              }}
              onDrop={(e) => {
                e.preventDefault();
                handleSelectedPhotoDrop(idx);
              }}
              onDragEnd={() => setDraggedPhotoIndex(-1)}
            >
              {f.thumbnail_url ? (
                <img
                  src={f.thumbnail_url}
                  alt={f.name || `Photo ${idx + 1}`}
                  className="h-28 w-full object-cover"
                />
              ) : (
                <div className="flex h-28 w-full items-center justify-center text-xs text-zinc-500">
                  No preview
                </div>
              )}
              <div className="space-y-2 border-t border-zinc-200 p-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-xs font-medium text-zinc-700">
                    {idx + 1}. {f.name || `Photo ${idx + 1}`}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-zinc-400">Drag</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => moveSelectedPhoto(idx, idx - 1)}
                    disabled={idx === 0 || state.uploadingPhotos}
                  >
                    Move Up
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => moveSelectedPhoto(idx, idx + 1)}
                    disabled={idx === state.photos.length - 1 || state.uploadingPhotos}
                  >
                    Move Down
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeSelectedPhoto(idx)}
                    disabled={state.uploadingPhotos}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="wizard-muted">No photos selected yet.</p>
      )}
      {state.uploadingPhotos ? (
        <div className="wizard-uploading-indicator">
          <Spinner size="sm" />
          <span className="wizard-muted">Uploading... {Math.round(state.uploadProgress || 0)}%</span>
        </div>
      ) : null}
      {state.processingError ? <p className="wizard-warning">{state.processingError}</p> : null}
      <div className="wizard-actions">
        <Button
          onClick={handleA0Continue}
          disabled={state.photos.length < 1 || !String(state.jobInfo?.jobName || "").trim() || state.uploadingPhotos}
        >
          Continue
        </Button>
      </div>
      </CardBody>
    </Card>
  );

  const renderA1 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Options</h1>
        <p className="wizard-subtitle">Choose optional enhancements before processing.</p>
      </CardHeader>
      <CardBody>
      {state.photoSyncError ? <p className="wizard-warning">{state.photoSyncError}</p> : null}
      <div className="wizard-options-block">
        <p className="wizard-subtitle">Do you want to label photo(s)?</p>
        <div className="wizard-actions">
          <Button
            variant={state.wantsPhotoLabels === false ? "primary" : "secondary"}
            onClick={() => setState((prev) => ({ ...prev, wantsPhotoLabels: false }))}
          >
            No
          </Button>
          <Button
            variant={state.wantsPhotoLabels === true ? "primary" : "secondary"}
            onClick={() => setState((prev) => ({ ...prev, wantsPhotoLabels: true }))}
          >
            Yes
          </Button>
        </div>
      </div>
      <div className="wizard-options-block">
        <p className="wizard-subtitle">Do you want line item suggestions?</p>
        <div className="wizard-actions">
          <Button
            variant={state.wantsLineItemSuggestions === false ? "primary" : "secondary"}
            onClick={() =>
              setState((prev) => ({
                ...prev,
                wantsLineItemSuggestions: false,
                wantsCodeUpgrades: false,
                propertyZip: "",
                processingError: "",
              }))
            }
          >
            No
          </Button>
          <Button
            variant={state.wantsLineItemSuggestions === true ? "primary" : "secondary"}
            onClick={() =>
              setState((prev) => ({
                ...prev,
                wantsLineItemSuggestions: true,
                wantsCodeUpgrades:
                  typeof prev.wantsCodeUpgrades === "boolean" ? prev.wantsCodeUpgrades : false,
                processingError: "",
              }))
            }
          >
            Yes
          </Button>
        </div>
      </div>
      {state.wantsLineItemSuggestions ? (
        <div className="wizard-options-block">
          <p className="wizard-subtitle">Do you want property code upgrades applied?</p>
          <div className="wizard-actions">
            <Button
              variant={state.wantsCodeUpgrades === false ? "primary" : "secondary"}
              onClick={() => setState((prev) => ({ ...prev, wantsCodeUpgrades: false, propertyZip: "" }))}
            >
              No
            </Button>
            <Button
              variant={state.wantsCodeUpgrades === true ? "primary" : "secondary"}
              onClick={() => setState((prev) => ({ ...prev, wantsCodeUpgrades: true }))}
            >
              Yes
            </Button>
          </div>
        {state.wantsCodeUpgrades ? (
          <div style={{ marginTop: 10 }}>
            <Input
              className="wizard-input"
              value={state.propertyZip}
              placeholder="Property ZIP code (e.g., 75001)"
              onChange={(e) => setState((prev) => ({ ...prev, propertyZip: e.target.value }))}
            />
            <p className="wizard-muted" style={{ marginTop: 6 }}>
              The app will use this ZIP to look up current code signals and apply code-compliance upgrade suggestions.
            </p>
          </div>
        ) : null}
        </div>
      ) : null}
      <div className="wizard-options-block">
        <p className="wizard-subtitle">Type of loss</p>
        <div className="wizard-actions">
          {LOSS_TYPE_OPTIONS.map((loss) => (
            <Button
              key={loss}
              variant={state.lossType === loss ? "primary" : "secondary"}
              onClick={() => setState((prev) => ({ ...prev, lossType: loss, processingError: "" }))}
            >
              {toTitleCase(loss)}
            </Button>
          ))}
        </div>
      </div>
      <AiDisclaimer
        title="AI + Public Data Disclaimer"
        detail="Loss classification, code upgrades, and public-safety contacts use AI and third-party data. Verify jurisdiction, code authority, and contact details before acting."
      />
      <div className="wizard-actions">
        <Button
          onClick={handleA1Continue}
          disabled={
            state.wantsPhotoLabels === null ||
            state.wantsLineItemSuggestions === null ||
            !LOSS_TYPE_OPTIONS.includes(String(state.lossType || "").toLowerCase()) ||
            (state.wantsLineItemSuggestions &&
              (state.wantsCodeUpgrades === null ||
                (state.wantsCodeUpgrades && !/^\d{5}(-\d{4})?$/.test(String(state.propertyZip || "").trim()))))
          }
        >
          Continue
        </Button>
      </div>
      </CardBody>
    </Card>
  );

  const renderA2 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Estimating Guidelines Upload (optional)</h1>
        <p className="wizard-subtitle">Select a saved guideline or upload a new one.</p>
        <div style={{ marginTop: 8 }}>
          <Link to="/app/guidelines">
            <Button variant="ghost" size="sm">Open Guidelines Library</Button>
          </Link>
        </div>
      </CardHeader>
      <CardBody>
      {state.availableGuidelines.length > 0 ? (
        <div className="wizard-actions" style={{ marginTop: 0 }}>
          <select
            className="wizard-input"
            value={state.selectedGuidelineId}
            onChange={(e) =>
              setState((prev) => ({
                ...prev,
                selectedGuidelineId: e.target.value,
                guidelineDocumentId: e.target.value || prev.guidelineDocumentId,
                guidelineSkipped: !e.target.value,
              }))
            }
          >
            <option value="">Choose from saved guidelines</option>
            {state.availableGuidelines.map((g) => (
              <option key={g.id} value={g.id}>
                {g.filename}
              </option>
            ))}
          </select>
          <Button variant="secondary" onClick={handleDeleteSelectedGuideline} disabled={!state.selectedGuidelineId}>
            Delete Saved Guideline
          </Button>
        </div>
      ) : null}
      <p className="wizard-muted">Or upload a guidelines file (PDF, DOC, DOCX, or TXT).</p>
      <input
        ref={guidelineInputRef}
        id="wizard-guidelines-upload"
        type="file"
        accept=".pdf,.doc,.docx,.txt,text/plain,application/pdf"
        className="wizard-hidden-input"
        onChange={handleGuidelineChange}
      />
      <label className="wizard-upload-button small" htmlFor="wizard-guidelines-upload">
        Upload Guidelines
      </label>
      {state.guideline ? (
        <div className="wizard-file-pill">
          <span>{state.guideline.name}</span>
        </div>
      ) : (
        <p className="wizard-muted">No guidelines uploaded.</p>
      )}
      <div className="wizard-actions">
        <Button variant="secondary" onClick={skipGuideline}>
          Skip
        </Button>
        <Button onClick={handleA2Continue}>
          Continue
        </Button>
      </div>
      </CardBody>
    </Card>
  );

  const renderA3 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Platform Select</h1>
      </CardHeader>
      <CardBody>
      <div className="wizard-platform-grid">
        <RadioCard
          name="platform"
          value="xactimate"
          title="Xactimate"
          description="Use Xactimate line item codes."
          checked={state.platform === "xactimate"}
          onChange={() => setState((prev) => ({ ...prev, platform: "xactimate" }))}
        />
        <RadioCard
          name="platform"
          value="symbility"
          title="Symbility"
          description="Use Symbility line item codes."
          checked={state.platform === "symbility"}
          onChange={() => setState((prev) => ({ ...prev, platform: "symbility" }))}
        />
      </div>
      <div className="wizard-actions">
        <Button onClick={handleA3Continue} disabled={!state.platform}>
          Continue
        </Button>
      </div>
      </CardBody>
    </Card>
  );

  const renderA4 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Sketch Upload (optional)</h1>
      </CardHeader>
      <CardBody>
      <p className="wizard-warning">Measurements may not be accurate without a sketch.</p>
      <input
        ref={sketchInputRef}
        id="wizard-sketch-upload"
        type="file"
        accept=".pdf,image/*"
        className="wizard-hidden-input"
        onChange={handleSketchChange}
      />
      <label className="wizard-upload-button small" htmlFor="wizard-sketch-upload">
        Upload Sketch
      </label>
      {state.sketch ? (
        <div className="wizard-file-pill">
          <span>{state.sketch.name}</span>
        </div>
      ) : (
        <p className="wizard-muted">No sketch uploaded.</p>
      )}
      <div className="wizard-actions">
        <Button variant="secondary" onClick={skipSketch}>
          Skip
        </Button>
        <Button onClick={handleA4Continue}>
          Continue
        </Button>
      </div>
      </CardBody>
    </Card>
  );

  const renderA5 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Status / Processing</h1>
      </CardHeader>
      <CardBody>
      <div className="wizard-progress-wrap">
        <Progress value={state.processingProgress || 0} />
        <span className="wizard-muted">{Math.round(state.processingProgress || 0)}%</span>
        <Badge tone="info">{state.processingPhase}</Badge>
      </div>
      <ul className="wizard-processing-list">
        {PROCESS_PHASES.map((phase) => {
          const current = phaseIndex(state.processingPhase);
          const idx = phaseIndex(phase);
          const isDone = idx < current || (state.processingDone && phase === "Ready");
          const isActive = idx === current && !isDone;
          return (
            <li key={phase} className={`wizard-processing-item${isDone ? " done" : ""}`}>
              <span className={`wizard-processing-icon${isActive ? " active" : ""}`}>
                {isDone ? "OK" : <Spinner size="sm" />}
              </span>
              <span>{phase}</span>
            </li>
          );
        })}
      </ul>
      <p className="wizard-muted">{state.processingMessage || "Processing..."}</p>
      {state.processingError ? <p className="wizard-warning">{state.processingError}</p> : null}
      <div className="wizard-actions">
        {state.processingError ? (
          <Button onClick={retryBuild}>
            Retry
          </Button>
        ) : null}
      </div>
      </CardBody>
    </Card>
  );

  const renderA6 = () => (
    <Card className="wizard-card">
      <CardHeader className="flex items-center justify-between gap-3">
        <h1 className="wizard-title">Label Review</h1>
        {state.packReady ? <Badge tone="success">Ready</Badge> : null}
      </CardHeader>
      <CardBody>
      <div className="wizard-summary-grid">
        <div>
          <strong>Job</strong>
          <p>{state.jobId || "Not created"}</p>
        </div>
        <div>
          <strong>Photos</strong>
          <p>
            {state.photos.length} selected ({state.photoIds.length} uploaded)
          </p>
        </div>
        <div>
          <strong>Photo Labels</strong>
          <p>
            {labelCompleteness.complete}/{labelCompleteness.total} complete
          </p>
        </div>
        <div>
          <strong>Line Item Suggestions</strong>
          <p>{state.wantsLineItemSuggestions ? "Enabled" : "Skipped"}</p>
        </div>
        <div>
          <strong>Guidelines</strong>
          <p>{state.guidelineDocumentId ? "Uploaded" : state.guidelineSkipped ? "Skipped" : "Not provided"}</p>
        </div>
        <div>
          <strong>Sketch</strong>
          <p>{state.sketchDocumentId ? "Uploaded" : state.sketchSkipped ? "Skipped" : "Not provided"}</p>
        </div>
        <div>
          <strong>Platform</strong>
          <p>{state.platform || "Not selected"}</p>
        </div>
        <div>
          <strong>Code Upgrades</strong>
          <p>
            {state.wantsLineItemSuggestions && state.wantsCodeUpgrades
              ? `Enabled${state.propertyZip ? ` (ZIP ${state.propertyZip})` : ""}`
              : "Disabled"}
          </p>
        </div>
        <div>
          <strong>Loss Type</strong>
          <p>{state.lossType ? toTitleCase(state.lossType) : "Not selected"}</p>
        </div>
      </div>

      <div className="wizard-labels-section">
        <h3 className="wizard-subtitle">Job Information</h3>
        <div className="wizard-photo-meta-grid">
          <Input
            value={state.jobInfo?.jobName || ""}
            placeholder="Job name"
            onChange={(e) => handleJobInfoChange("jobName", e.target.value)}
          />
          <Input
            value={state.jobInfo?.insuredName || ""}
            placeholder="Insured / customer name"
            onChange={(e) => handleJobInfoChange("insuredName", e.target.value)}
          />
          <Input
            value={state.jobInfo?.claimNumber || ""}
            placeholder="Claim number"
            onChange={(e) => handleJobInfoChange("claimNumber", e.target.value)}
          />
          <Input
            className="sm:col-span-3"
            value={state.jobInfo?.propertyAddress || ""}
            placeholder="Property address"
            onChange={(e) => handleJobInfoChange("propertyAddress", e.target.value)}
          />
          <Input
            className="sm:col-span-3"
            value={state.jobInfo?.notes || ""}
            placeholder="Notes"
            onChange={(e) => handleJobInfoChange("notes", e.target.value)}
          />
        </div>
        <div className="wizard-actions">
          <Button variant="secondary" onClick={handleSaveJobInfo}>Save Job Information</Button>
          {state.jobInfo?.savedAt ? (
            <Badge tone="success">Saved {new Date(state.jobInfo.savedAt).toLocaleString()}</Badge>
          ) : null}
        </div>
      </div>
      <AiDisclaimer
        title="Photo Labeling Disclaimer"
        detail="Room, damage type, description, and confidence are AI-assisted estimates. Confirm and edit labels before finalizing."
      />

      {CIVIC_CONTACT_LOSS_TYPES.has(String(state.lossType || "").toLowerCase()) ? (
        <div className="wizard-labels-section">
          <h3 className="wizard-subtitle">Emergency Contacts</h3>
          {publicSafetyLoading ? (
            <p className="wizard-muted inline-flex items-center gap-2"><Spinner size="sm" />Loading contacts...</p>
          ) : publicSafetyContacts.length > 0 ? (
            <ul className="wizard-label-list">
              {publicSafetyContacts.map((contact, idx) => (
                <li key={`${contact.type || "agency"}-${contact.name || "contact"}-${idx}`} className="wizard-label-row">
                  <div className="wizard-label-main" style={{ display: "block" }}>
                    <div className="wizard-photo-name">{contact.name || toTitleCase(contact.type || "Agency")}</div>
                    <p className="wizard-muted">
                      {contact.phone ? `Phone: ${contact.phone}` : "Phone: not listed"}
                    </p>
                    <p className="wizard-muted">
                      {contact.address ? `Address: ${contact.address}` : "Address: not listed"}
                    </p>
                    {Number.isFinite(Number(contact.distance_miles)) ? (
                      <p className="wizard-muted">Distance: {Number(contact.distance_miles).toFixed(2)} mi</p>
                    ) : null}
                    <div className="wizard-actions">
                      {contact.website ? (
                        <a href={contact.website} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="secondary">Open Website</Button>
                        </a>
                      ) : null}
                      {contact.source ? <Badge tone="info">{contact.source}</Badge> : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : publicSafetyError ? (
            <p className="wizard-warning">{publicSafetyError}</p>
          ) : (
            <p className="wizard-muted">
              Add a property ZIP and/or address above to look up fire or police contact details.
            </p>
          )}
          {publicSafetyResolvedLocation ? (
            <p className="wizard-muted" style={{ marginTop: 8 }}>Resolved location: {publicSafetyResolvedLocation}</p>
          ) : null}
        </div>
      ) : null}

      {state.wantsPhotoLabels && summaryPhotoRows.length > 0 ? (
        <div className="wizard-labels-section">
          <h3 className="wizard-subtitle">Photo Labels</h3>
          <div className="wizard-progress-wrap">
            <Progress value={labelCompleteness.percent} />
            <Badge tone={labelCompleteness.remaining > 0 ? "warning" : "success"}>
              {labelCompleteness.complete}/{labelCompleteness.total}
            </Badge>
          </div>
          <p className="wizard-muted">
            {labelCompleteness.remaining > 0
              ? `${labelCompleteness.remaining} photo(s) still need room, damage type, and description.`
              : "All photo labels are complete."}
          </p>
          {state.photoSyncError ? (
            <p className="wizard-warning">{state.photoSyncError}</p>
          ) : null}
          <ul className="wizard-label-list">
            {summaryPhotoRows.map((photo, index) => {
              const draft = photoEditDrafts[photo.id] || {};
              const saveState = photoSaveState[photo.id] || { saving: false, reanalyzing: false, error: "" };
              const room = Object.prototype.hasOwnProperty.call(draft, "room") ? draft.room : photo.room || "";
              const damageType = Object.prototype.hasOwnProperty.call(draft, "damage_type")
                ? draft.damage_type
                : photo.damage_type || "";
              const description = Object.prototype.hasOwnProperty.call(draft, "description")
                ? draft.description
                : photo.description || "";
              return (
                <li key={`${photo.id}-${index}`} className="wizard-label-row">
                  <div className="wizard-label-main">
                    {photoBlobUrls[photo.id] || photo.thumbnail_url ? (
                      <button
                        type="button"
                        className="wizard-photo-thumb-btn"
                        onClick={() =>
                          setPreviewImage({
                            url: photoBlobUrls[photo.id] || photo.thumbnail_url,
                            name: photo.filename || `Photo ${index + 1}`,
                          })
                        }
                      >
                        <img
                          className="wizard-photo-thumb"
                          src={photoBlobUrls[photo.id] || photo.thumbnail_url}
                          alt={photo.filename || `Photo ${index + 1}`}
                        />
                      </button>
                    ) : (
                      <div className="wizard-photo-thumb wizard-photo-thumb-empty">No preview</div>
                    )}
                    <div>
                      <div className="wizard-photo-name">{photo.filename || `Photo ${index + 1}`}</div>
                      <code>
                        Room: {formatRoomName(room || "(room not set)")} | Damage Type: {damageType || "(damage type not set)"} | Damage: {description || "(description not set)"}
                      </code>
                      <p className="wizard-muted" style={{ margin: "6px 0 0" }}>
                        Confidence: {typeof photo.confidence === "number" && photo.confidence > 0 ? `${Math.round(photo.confidence * 100)}%` : "N/A"}
                      </p>
                      <div className="wizard-actions" style={{ marginTop: 6 }}>
                        <Badge tone={String(photo.analysis_source || "").toLowerCase() === "vision_model" ? "success" : "warning"}>
                          {photo.analysis_source || "fallback"}
                        </Badge>
                        {photo.analysis_error ? <span className="wizard-muted">({photo.analysis_error})</span> : null}
                      </div>
                      <div className="wizard-photo-meta-grid" style={{ marginTop: 8 }}>
                        <Input
                          className="wizard-input"
                          value={room}
                          placeholder="Room"
                          onChange={(e) => handlePhotoDraftChange(photo.id, "room", e.target.value)}
                        />
                        <Input
                          className="wizard-input"
                          value={damageType}
                          placeholder="Damage type"
                          onChange={(e) => handlePhotoDraftChange(photo.id, "damage_type", e.target.value)}
                        />
                        <Input
                          className="wizard-input"
                          value={description}
                          placeholder="Description"
                          onChange={(e) => handlePhotoDraftChange(photo.id, "description", e.target.value)}
                        />
                      </div>
                      <div className="wizard-actions">
                        <Button
                          variant="secondary"
                          onClick={() => handleSavePhoto(photo)}
                          disabled={saveState.saving || saveState.reanalyzing}
                        >
                          {saveState.saving ? "Saving..." : "Save"}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => handleReanalyze(photo)}
                          disabled={saveState.saving || saveState.reanalyzing}
                        >
                          {saveState.reanalyzing ? "Re-analyzing..." : "Re-analyze"}
                        </Button>
                        {saveState.error ? <span className="wizard-warning">{saveState.error}</span> : null}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      handleCopyLabel(
                        `Room: ${formatRoomName(room || "")} | Damage Type: ${damageType || ""} | Damage: ${description || ""}`,
                        index
                      )
                  }
                >
                  {copiedLabelIndex === index ? "Copied" : "Copy"}
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      {state.wantsPhotoLabels && summaryPhotoRows.length < 1 ? (
        <p className="wizard-warning">
          No backend photo labels available yet. Re-upload photos to sync with backend.
        </p>
      ) : null}

      <div className="wizard-actions">
        {state.wantsPhotoLabels && summaryPhotoRows.length > 1 ? (
          <Button variant="secondary" onClick={handleSaveAllPhotos} disabled={!hasUnsavedPhotoDrafts}>
            Save All Labels
          </Button>
        ) : null}
        {state.wantsLineItemSuggestions ? (
          <>
            <Button
              onClick={() => setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A7 }))}
              disabled={hasUnsavedPhotoDrafts}
            >
              Review Line Items
            </Button>
            <Button
              variant="secondary"
              onClick={handleRetryPackCheck}
              disabled={!state.jobId || packCheckRetrying}
            >
              {packCheckRetrying ? "Checking Pack..." : "Retry Pack Check"}
            </Button>
            <Button
              onClick={handleDownloadPack}
              disabled={!state.packReady || state.downloadingPack || !state.jobId}
            >
              {state.downloadingPack ? "Preparing Download..." : "Download Estimator Pack (.zip)"}
            </Button>
          </>
        ) : null}
        <Button variant="secondary" onClick={startAnotherBuild}>
          Start another build
        </Button>
      </div>
      {state.processingError ? <p className="wizard-warning">{state.processingError}</p> : null}
      </CardBody>
    </Card>
  );

  const renderA7 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Line Item Review</h1>
        <p className="wizard-subtitle">
          Review suggested line items and copy the line item code for your estimating platform.
        </p>
      </CardHeader>
      <CardBody>
      <AiDisclaimer
        title="Line Item Suggestion Disclaimer"
        detail="Suggested line items and quantities are AI-assisted and may miss scope or include inaccuracies. Validate against site conditions and carrier requirements."
      />
      {state.wantsLineItemSuggestions === false ? (
        <p className="wizard-muted">Line item suggestions were skipped for this build.</p>
      ) : state.lineItemsLoading ? (
        <p className="wizard-muted inline-flex items-center gap-2"><Spinner size="sm" />Loading suggested line items...</p>
      ) : state.lineItems.length < 1 && state.lineItemsConfirmedEmpty ? (
        <p className="wizard-warning">No suggested line items found yet. Run processing again or retry.</p>
      ) : (
        <div className="space-y-4">
          {groupedLineItemsByRoom.map((roomGroup) => (
            <div key={roomGroup.room} className="space-y-3">
              <h3 className="text-xl font-semibold tracking-tight text-zinc-900" style={{ marginTop: 0 }}>
                Room: {formatRoomName(roomGroup.room)}
              </h3>
              {roomGroup.areas.map((areaGroup) => (
                <div key={`${roomGroup.room}-${areaGroup.area}`} style={{ overflowX: "auto" }}>
                  <h4 className="wizard-muted" style={{ margin: "0 0 8px" }}>
                    {areaGroup.area}
                  </h4>
                  <table className="wizard-lineitems-table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Description</th>
                        <th>Qty</th>
                        <th>Unit</th>
                        <th>Copy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {areaGroup.items.map((item, index) => (
                        <tr key={`${roomGroup.room}-${areaGroup.area}-${item.key}`}>
                          <td><code>{item.code || "(none)"}</code></td>
                          <td>{item.description || ""}</td>
                          <td>{item.qty === "" ? "" : String(item.qty)}</td>
                          <td>{item.unit || ""}</td>
                          <td>
                            <Button size="sm" variant="secondary" onClick={() => handleCopyLineItemCode(item.code, index)}>
                              {state.copiedCodeIndex === index ? "Copied" : "Copy Code"}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={handleReanalyze} className="ml-2">
                              Re-analyze
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      <div className="wizard-actions">
        <Button variant="secondary" onClick={() => setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A6 }))}>
          Back to Label Review
        </Button>
        <Button variant="ghost" onClick={handleReanalyze}>
          Re-analyze
        </Button>
        <Button
          variant="secondary"
          onClick={handleRetryPackCheck}
          disabled={!state.jobId || packCheckRetrying}
        >
          {packCheckRetrying ? "Checking Pack..." : "Retry Pack Check"}
        </Button>
        <Button
          onClick={handleDownloadPack}
          disabled={!state.packReady || state.downloadingPack || !state.jobId}
        >
          {state.downloadingPack ? "Preparing Download..." : "Download Estimator Pack (.zip)"}
        </Button>
      </div>
      </CardBody>
    </Card>
  );

  let stepContent = null;
  if (state.currentStepId === STEP_IDS.A0) stepContent = renderA0();
  else if (state.currentStepId === STEP_IDS.A1) stepContent = renderA1();
  else if (state.currentStepId === STEP_IDS.A2) stepContent = renderA2();
  else if (state.currentStepId === STEP_IDS.A3) stepContent = renderA3();
  else if (state.currentStepId === STEP_IDS.A4) stepContent = renderA4();
  else if (state.currentStepId === STEP_IDS.A5) stepContent = renderA5();
  else if (state.currentStepId === STEP_IDS.A6) stepContent = renderA6();
  else stepContent = renderA7();

  return (
    <div className="build-wizard-page">
      {flash.text ? <div className={`wizard-flash ${flash.type === "error" ? "error" : "success"}`}>{flash.text}</div> : null}
      <div className="wizard-top-actions">
        <Button variant="secondary" onClick={handleCancelBuild}>
          Cancel Build
        </Button>
      </div>
      <WizardStepper steps={WIZARD_STEPS} currentStepId={state.currentStepId} onStepClick={handleStepJump} />
      {stepContent}
      {previewImage ? (
        <div className="wizard-image-modal" role="dialog" aria-modal="true" onClick={() => setPreviewImage(null)}>
          <div className="wizard-image-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="wizard-image-modal-head">
              <span className="wizard-photo-name">{previewImage.name}</span>
              <Button size="sm" variant="secondary" onClick={() => setPreviewImage(null)}>
                Close
              </Button>
            </div>
            <img className="wizard-image-modal-img" src={previewImage.url} alt={previewImage.name} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
